/* MENASA contact, letter v2 (4 October 2026). The letter's paper and its whole journey drawn in one WebGL 2 canvas behind
   the letter (sections/menasa-contact.liquid with "Letter style: v2"; v1 is the CSS envelope in menasa-pages.js).
   The paper is a cotton sheet made by a shader (formation, tooth, fibres, deckled edge) with printed stationery: a foil
   frame, the crest letterhead, writing lines on the message's own baselines, a watermark. Sent: the subject stamp is
   lifted off, the letter folds (in half, or in three if long), a lined envelope comes beneath it, the letter slides in
   and the flap closes; the name and email are written on the flap; wax is poured from a brass spoon. Pressed (after
   Shopify's spam check, which menasa-pages.js runs as for v1), a seal stamp modelled by revolving a turned profile
   (ebony and brass) brings the crest down; the envelope turns to its foil front, takes the stamp and a postmark and
   flies off; the crest and "Received by the house" stay pressed into the page. Six waxes, one per letter.
   menasa-pages.js drives it through create(form, options): start, checking, press, send, rewind, hover, geometry,
   destroy. Pictures (wax, spoon) are fetched only once the visitor starts on the letter. Prototype and measurements:
   prototype/contact-envelope/, design/contact-envelope-2026-10-04/. */
(() => {
  'use strict';
  if (window.MenasaLetterV2) return;

  // ── small maths ──────────────────────────────────────────────────────────────────────────────
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const seg = (t, a, b) => clamp((t - a) / (b - a));
  const lerp = (a, b, t) => a + (b - a) * t;
  const hex = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16) / 255);
  const E = {
    io: t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    out: t => 1 - Math.pow(1 - t, 3),
    out5: t => 1 - Math.pow(1 - t, 5),
    in: t => t * t * t,
    in2: t => t * t,
    sine: t => (1 - Math.cos(Math.PI * t)) / 2
  };
  const M4 = {
    I: () => new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]),
    mul(a, b) { const o = new Float32Array(16); for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) { let s = 0; for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]; o[c * 4 + r] = s; } return o; },
    T(x, y, z = 0) { const m = M4.I(); m[12] = x; m[13] = y; m[14] = z; return m; },
    S(x, y = x, z = 1) { const m = M4.I(); m[0] = x; m[5] = y; m[10] = z; return m; },
    RX(a) { const c = Math.cos(a), s = Math.sin(a); return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]); },
    RY(a) { const c = Math.cos(a), s = Math.sin(a); return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]); },
    RZ(a) { const c = Math.cos(a), s = Math.sin(a); return new Float32Array([c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]); },
    apply(m, x, y, z = 0) { return [m[0] * x + m[4] * y + m[8] * z + m[12], m[1] * x + m[5] * y + m[9] * z + m[13], m[2] * x + m[6] * y + m[10] * z + m[14]]; }
  };
  const chain = (...ms) => ms.reduce((a, b) => M4.mul(a, b));
  // The bend the vertex shader does, for shadows: a sheet folding over a hinge round a cylinder of radius R, its free
  // part lagging a little behind (curvature k), as paper does in the air.
  function bendPt(x, y, f, dir) {
    const u = dir < 0 ? f[0] - y : y - f[0];
    if (u <= 0 || f[1] <= 1e-4) return [x, y, 0];
    const th = f[1], R = Math.max(f[2], 1e-3), k = f[3], arc = R * th;
    let dist, z;
    if (u <= arc) { const p = u / R; dist = R * Math.sin(p); z = R * (1 - Math.cos(p)); }
    else {
      const s = u - arc, d0 = R * Math.sin(th), z0 = R * (1 - Math.cos(th));
      if (Math.abs(k) < 1e-6) { dist = d0 + s * Math.cos(th); z = z0 + s * Math.sin(th); }
      else { const p = th - k * s; dist = d0 + (Math.sin(th) - Math.sin(p)) / k; z = z0 + (Math.cos(p) - Math.cos(th)) / k; }
    }
    return [x, f[0] + (dir < 0 ? -dist : dist), z];
  }
  function hull(pts) {
    const p = pts.slice().sort((a, b) => a.x - b.x || a.y - b.y);
    if (p.length < 3) return p;
    const cr = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lo = [], up = [];
    for (const q of p) { while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
    for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
    lo.pop(); up.pop();
    let h = lo.concat(up);
    while (h.length > 16) h = h.filter((_, i) => i % 2 === 0);
    return h;
  }
  function cubic(a, c1, c2, b, n) {
    const out = [];
    for (let i = 1; i <= n; i++) { const t = i / n, m = 1 - t; out.push([0, 1].map(k => m * m * m * a[k] + 3 * m * m * t * c1[k] + 3 * m * t * t * c2[k] + t * t * t * b[k])); }
    return out;
  }
  // The flap, drawn open: above its hinge (y < 0), a point with a rounded tip.
  function flapShape(EW, FHl, rt) {
    const half = EW / 2, len = Math.hypot(half, FHl), uc = FHl - rt * len / half, ut = uc + rt * half / len, tip = uc + rt;
    const w = u => (u <= ut ? half * (1 - u / FHl) : Math.sqrt(Math.max(0, rt * rt - (u - uc) * (u - uc))));
    return { half, len, tip, w };
  }
  const mkCanvas = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, Math.ceil(w)); c.height = Math.max(1, Math.ceil(h)); return c; };
  const pictures = new Map();
  const loadImg = src => {
    if (!src) return Promise.resolve(null);
    if (!pictures.has(src)) pictures.set(src, new Promise(res => { const i = new Image(); i.decoding = 'async'; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; }));
    return pictures.get(src);
  };
  function tinted(img, w, h, color) {
    const c = mkCanvas(w, h), x = c.getContext('2d');
    if (img) x.drawImage(img, 0, 0, c.width, c.height);
    x.globalCompositeOperation = 'source-in'; x.fillStyle = color; x.fillRect(0, 0, c.width, c.height);
    return c;
  }
  // Ink for a CSS colour on the paper: the darker, the more ink.
  function inkAlpha(css) {
    const m = css.match(/[\d.]+/g); if (!m) return 1;
    const [r, g, b] = m.slice(0, 3).map(Number), a = m[3] !== undefined ? +m[3] : 1;
    const lum = (.299 * r + .587 * g + .114 * b) / 255;
    return clamp((.93 - lum) / (.93 - .11)) * a;
  }

  // ── the six waxes ────────────────────────────────────────────────────────────────────────────
  // Each a molten pool and the same pool pressed (theme assets menasa-letter-wax-N-pool/-seal.webp), measured 4 October
  // 2026: the pool's centre and radius (cx, cy, r: the pour grows from there) by its opaque area; the impression's
  // centre and radius (wx, wy, well: the seal stamp's face lands exactly on it) by a circle search on its rim; tint, the
  // molten wax in the spoon. Oxblood, black cherry, oxblood with gold mica, midnight navy, antique gold, a burgundy spill.
  const WAX = [
    { cx: .498, cy: .504, r: .421, wx: .49, wy: .495, well: .2775, tint: '#7d221b' },
    { cx: .504, cy: .495, r: .349, wx: .5, wy: .5, well: .24, tint: '#4a1714' },
    { cx: .505, cy: .5, r: .405, wx: .495, wy: .495, well: .2675, tint: '#8a2a1e' },
    { cx: .5, cy: .502, r: .424, wx: .49, wy: .5, well: .2575, tint: '#1f2a4d' },
    { cx: .505, cy: .508, r: .425, wx: .49, wy: .49, well: .2625, tint: '#b08a4a' },
    { cx: .515, cy: .5, r: .404, wx: .49, wy: .485, well: .24, tint: '#6a1b22' }
  ];
  const SPOON = { lip: [.086, .909], bowl: [.271, .746], r: .183, end: [.95, .096] };
  // The seal stamp, turned: [radius, height, 0 brass / 1 ebony] in units of its face's diameter. A brass die drum with a
  // groove, a shoulder and collar; an ebony bead, a slender neck and a round knob. Revolved into a mesh below.
  const PROFILE = [
    [0, 0, 0], [.47, 0, 0], [.5, .03, 0], [.5, .09, 0], [.483, .102, 0], [.5, .114, 0], [.5, .19, 0], [.48, .22, 0], [.42, .245, 0],
    [.34, .27, 0], [.27, .32, 0], [.22, .41, 0], [.205, .48, 0],
    [.24, .5, 0], [.24, .58, 0], [.205, .6, 0],
    [.19, .61, 1], [.205, .65, 1], [.26, .7, 1], [.26, .76, 1], [.2, .82, 1], [.165, .95, 1], [.15, 1.15, 1], [.155, 1.4, 1],
    [.185, 1.62, 1], [.235, 1.82, 1], [.285, 2.02, 1], [.32, 2.2, 1], [.335, 2.36, 1], [.32, 2.52, 1], [.27, 2.66, 1], [.19, 2.76, 1],
    [.1, 2.81, 1], [0, 2.83, 1]
  ];
  const COL = {
    letter: hex('#f7f1e5'), letterBack: hex('#f3ecdf'), env: hex('#efe5d1'), envInside: hex('#e8dcc5'),
    gum: hex('#ece0c4'), ink: hex('#211b17'), postmark: hex('#2a201b'), shadow: [.16, .11, .07], tint: hex('#8d7255'), rule: hex('#9c8467')
  };
  const LIGHT = (() => { const v = [-.42, -.62, 1]; const l = Math.hypot(...v); return v.map(x => x / l); })();
  const BLEED = 260, PERSP = 1800;
  function timeline(o) {
    return {
      foldA: o ? [.95, 1.6] : [.22, 1.3], foldB: [.5, 1.15], settle: o ? 1.6 : 1.3,
      lift: [1.4 + o, 2.1 + o], envIn: [1.3 + o, 2.1 + o], down: [2.1 + o, 2.36 + o], insert: [2.16 + o, 2.92 + o],
      flatten: [2.25 + o, 2.75 + o], flap: [2.88 + o, 3.52 + o], bounce: [3.52 + o, 3.92 + o],
      name: [3.5 + o, 4.15 + o], email: [4.05 + o, 4.6 + o],
      spoonIn: [4.0 + o, 4.46 + o], pour: [4.42 + o, 5.05 + o], spoonOut: [5.0 + o, 5.5 + o], molten: [4.5 + o, 7.2 + o],
      ready: 5.1 + o
    };
  }
  // After the press: the seal stamp (0–1.3 s), then, once Shopify has the letter, the turn, stamp, postmark and flight.
  const SP = {
    down: [0, .5], mix: [.5, .58], spread: [.5, .64], twist: [.52, .74], up: [.76, 1.3], toolFade: [1.05, 1.3], dip: [.5, .72],
    glint: [1.0, 1.55], hold: 1.6, lift: [1.65, 2.4], flip: [1.7, 2.4], stampIn: [2.3, 2.85], stampPress: [2.78, 2.98],
    postmark: 3.04, pmDip: [3.04, 3.2], flight: [3.3, 4.45], fade: [3.95, 4.45], relief: [3.65, 4.9], sweep: [3.8, 6.2],
    done: 4.4, end: 6.2
  };

  // ── shaders ──────────────────────────────────────────────────────────────────────────────────
  const HEAD = `#version 300 es
precision highp float;
uniform vec3 u_cam;
uniform vec2 u_half, u_off;
// the eye (u_cam) looks down from just below the seal; u_off (eye minus the canvas centre) keeps the page plane z = 0
// exactly where the page is, wherever the eye is
vec4 project(vec3 w) { float d = u_cam.z - w.z; return vec4(((w.x - u_cam.x) * u_cam.z + u_off.x * d) / u_half.x, -((w.y - u_cam.y) * u_cam.z + u_off.y * d) / u_half.y, 0.0, d); }
`;
  const VS_PAPER = HEAD + `
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec2 a_uv;
layout(location = 2) in float a_edge;
uniform mat4 u_model;
uniform vec4 u_foldA;
uniform vec4 u_foldB;
uniform float u_lift;
out vec2 v_uv; out vec2 v_obj; out vec3 v_w; out vec3 v_n; out vec3 v_t; out float v_edge;
void bend(inout vec3 p, inout vec3 n, vec4 f, float dir) {
  float u = dir < 0.0 ? f.x - p.y : p.y - f.x;
  if (u <= 0.0 || f.y <= 0.0001) return;
  float th = f.y; float R = max(f.z, 0.001); float k = f.w;
  float arc = R * th; float dist; float z; float phi;
  if (u <= arc) { phi = u / R; dist = R * sin(phi); z = R * (1.0 - cos(phi)); }
  else {
    float s = u - arc; float d0 = R * sin(th); float z0 = R * (1.0 - cos(th));
    if (abs(k) < 1e-6) { phi = th; dist = d0 + s * cos(th); z = z0 + s * sin(th); }
    else { phi = th - k * s; dist = d0 + (sin(th) - sin(phi)) / k; z = z0 + (cos(phi) - cos(th)) / k; }
  }
  p.y = f.x + (dir < 0.0 ? -dist : dist);
  p.z += z;
  n = vec3(0.0, -dir * sin(phi), cos(phi));
}
void main() {
  vec3 p = vec3(a_pos, 0.0); vec3 n = vec3(0.0, 0.0, 1.0);
  bend(p, n, u_foldA, -1.0);
  bend(p, n, u_foldB, 1.0);
  p += n * u_lift;
  vec4 w = u_model * vec4(p, 1.0);
  mat3 nm = mat3(u_model);
  v_n = normalize(nm * n); v_t = normalize(nm * vec3(1.0, 0.0, 0.0));
  v_w = w.xyz; v_uv = a_uv; v_obj = a_pos; v_edge = a_edge;
  gl_Position = project(w.xyz);
}`;
  // Materials: 1 paper (ink 1 the letter's words, 2 words seen through the sheet, 3 bronze foil; printing: frame,
  // letterhead, lines, watermark), 2 lining (gold eight-point star lattice on midnight blue), 3 printed picture (the
  // stamp), 4 envelope writing (foil + handwritten ink, written out), 5 postmark ink.
  const FS_PAPER = HEAD + `
in vec2 v_uv; in vec2 v_obj; in vec3 v_w; in vec3 v_n; in vec3 v_t; in float v_edge;
uniform sampler2D u_map; uniform sampler2D u_ink; uniform sampler2D u_print;
uniform float u_mapScale, u_bump, u_alpha, u_rim, u_deckle, u_alphaTex, u_wet, u_linerScale, u_printOn;
uniform vec3 u_light, u_colF, u_colB, u_inkCol, u_ruleCol;
uniform vec2 u_printTexel;
uniform int u_matF, u_matB, u_inkF, u_inkB;
uniform vec2 u_inkTexel, u_size;
uniform vec4 u_reveal;
out vec4 o;
float hgt(vec2 q) { vec4 t = texture(u_map, q); return t.r * 0.62 + t.b * 0.38; }
vec3 foil(float spec, float diff, float edge) {
  return mix(vec3(0.34, 0.25, 0.15), vec3(0.94, 0.81, 0.55), clamp(0.24 + spec * 2.6 + edge * 0.6 + (diff - 1.0) * 1.6, 0.0, 1.0));
}
void main() {
  vec3 N = normalize(v_n);
  vec3 V = normalize(u_cam - v_w);
  bool front = dot(N, V) >= 0.0;
  int mat = front ? u_matF : u_matB;
  if (mat == 0) discard;
  if (!front) N = -N;
  vec3 T = normalize(v_t - N * dot(v_t, N));
  vec3 B = cross(N, T);
  vec2 q = v_obj / u_mapScale;
  const float e = 1.0 / 1024.0;
  vec4 pm = texture(u_map, q);
  float hx = hgt(q + vec2(e, 0.0)) - hgt(q - vec2(e, 0.0));
  float hy = hgt(q + vec2(0.0, e)) - hgt(q - vec2(0.0, e));
  vec3 Np = normalize(N - (T * hx + B * hy) * u_bump);
  vec3 L = normalize(u_light);
  float ndl = max(dot(Np, L), 0.0);
  const float amb = 0.5;
  float diff = (amb + (1.0 - amb) * ndl) / (amb + (1.0 - amb) * L.z);
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(Np, H), 0.0), 36.0);
  vec2 uv = v_uv;
  vec2 uvm = vec2(1.0 - v_uv.x, v_uv.y);
  float alpha = u_alpha;
  vec3 col = vec3(0.0);
  if (mat == 1) {
    vec3 base = front ? u_colF : u_colB;
    vec3 alb = base * (1.0 + (pm.g - 0.5) * 0.08) * (1.0 + (pm.b - 0.2) * 0.06);
    col = alb * diff + vec3(spec * 0.03);
    if (u_printOn > 0.5) {
      vec4 pr = texture(u_print, front ? uv : uvm);
      col *= 1.0 + pr.b * 0.04;
      if (front) {
        col = mix(col, u_ruleCol * (0.88 + 0.12 * diff), pr.g * 0.5);
        float pl = texture(u_print, uv - u_printTexel).r, pa = texture(u_print, uv + u_printTexel).r;
        col = mix(col, foil(spec, diff, pa - pl), pr.r);
      }
    }
    int ink = front ? u_inkF : u_inkB;
    if (ink == 1) {
      // inkTooth: the ink thins on the paper's peaks, exactly as the page's mask did while the letter was written
      float m = texture(u_ink, uv).a * (1.0 - 0.5 * smoothstep(0.42, 0.68, pm.r));
      col = mix(col, u_inkCol * (0.82 + 0.18 * diff), m * 0.97) + vec3(m * spec * 0.05);
    } else if (ink == 2) {
      float m = texture(u_ink, uvm, 2.5).a;
      col *= 1.0 - m * 0.03;
    } else if (ink == 3) {
      float f = texture(u_ink, uvm).r;
      float fl = texture(u_ink, uvm - u_inkTexel).r;
      float fa = texture(u_ink, uvm + u_inkTexel).r;
      col = mix(col, foil(spec, diff, fa - fl), f);
    }
    if (u_alphaTex > 0.5) alpha *= texture(u_ink, front ? uv : uvm).a;
  } else if (mat == 2) {
    vec2 p = v_obj / u_linerScale;
    vec2 c = fract(p) - 0.5;
    float sq = max(abs(c.x), abs(c.y));
    vec2 r = vec2(c.x + c.y, c.x - c.y) * 0.70710678;
    float sq2 = max(abs(r.x), abs(r.y));
    float star = min(sq, sq2);
    float oct = max(sq, sq2);
    float fw = fwidth(star) * 1.25 + 1e-4;
    float g1 = 1.0 - smoothstep(0.018, 0.018 + fw, abs(star - 0.3535));
    float g2 = 1.0 - smoothstep(0.014, 0.014 + fw, abs(oct - 0.19));
    float g3 = 1.0 - smoothstep(0.03, 0.03 + fw, length(c));
    float gold = max(max(g1, g2 * 0.8), g3 * 0.9);
    vec2 cell = floor(p);
    float hv = fract(sin(dot(cell, vec2(12.9898, 78.233))) * 43758.5453);
    vec3 Ng = normalize(Np + (T * (hv - 0.5) + B * (fract(hv * 7.13) - 0.5)) * 0.5);
    float gs = pow(max(dot(Ng, H), 0.0), 16.0);
    vec3 navy = vec3(0.071, 0.098, 0.153) * (0.9 + pm.g * 0.2) * diff;
    vec3 gc = mix(vec3(0.46, 0.35, 0.21), vec3(0.97, 0.86, 0.62), clamp(0.3 + gs * 1.8 + (diff - 1.0) * 1.3, 0.0, 1.0));
    col = mix(navy, gc, gold);
  } else if (mat == 3) {
    vec4 t = texture(u_ink, front ? uv : uvm);
    if (t.a < 0.004) discard;
    col = t.rgb / t.a * diff + vec3(spec * 0.05);
    alpha *= t.a;
  } else if (mat == 4) {
    vec4 t = texture(u_ink, uv);
    float f = t.r; float hnd = t.g;
    float edge = uv.y < u_reveal.z ? u_reveal.x : u_reveal.y;
    hnd *= 1.0 - smoothstep(edge - 0.018, edge, uv.x);
    float a = clamp(f + hnd, 0.0, 1.0);
    if (a < 0.003) discard;
    float fl = texture(u_ink, uv - u_inkTexel).r; float fa = texture(u_ink, uv + u_inkTexel).r;
    vec3 fc = foil(spec, diff, fa - fl);
    vec3 ic = u_inkCol * (0.85 + 0.15 * diff) + vec3(spec * 0.3 * u_wet);
    col = (fc * f + ic * hnd) / max(f + hnd, 1e-4);
    alpha *= a;
  } else {
    float m = texture(u_ink, front ? uv : uvm).a;
    if (m < 0.003) discard;
    col = u_inkCol; alpha *= m;
  }
  if (u_deckle > 0.0) {
    vec2 d2 = min(v_obj, u_size - v_obj);
    bool sideX = d2.x < d2.y;
    float d = min(d2.x, d2.y);
    float along = sideX ? v_obj.y : v_obj.x;
    float n1 = texture(u_map, vec2(along / 640.0, sideX ? 0.13 : 0.61)).g;
    float n2 = texture(u_map, vec2(along / 70.0, sideX ? 0.37 : 0.83)).r;
    float edgeD = u_deckle * (0.3 + n1 * 0.9 + n2 * 0.6);
    alpha *= smoothstep(edgeD - 0.6, edgeD + 0.6, d);
    col *= mix(1.025, 1.0, smoothstep(edgeD, edgeD + 4.0, d));
  }
  if (u_rim > 0.0) { float rr = 1.0 - smoothstep(0.0, 1.6, v_edge); col *= 1.0 + rr * u_rim * (0.4 + ndl); }
  o = vec4(col * alpha, alpha);
}`;
  const VS_QUAD = HEAD + `
layout(location = 0) in vec2 a_pos;
uniform mat4 u_model;
out vec2 v_uv; out vec3 v_w; out vec3 v_n;
void main() {
  vec4 w = u_model * vec4(a_pos, 0.0, 1.0);
  v_uv = a_pos; v_w = w.xyz; v_n = normalize(mat3(u_model) * vec3(0.0, 0.0, 1.0));
  gl_Position = project(w.xyz);
}`;
  // Wax: the molten picture is poured in (a noisy circle grows), the pressed picture replaces it under the seal stamp,
  // the seal's word sinks into the soft wax on hover, a glint runs over the crest once set. Mode 0 draws a picture (the
  // spoon, its wax taking this letter's colour); mode 2 the stream of wax.
  const FS_SPRITE = HEAD + `
in vec2 v_uv; in vec3 v_w; in vec3 v_n;
uniform sampler2D u_tex, u_tex2, u_txt, u_map;
uniform int u_mode;
uniform float u_opacity, u_facing, u_mix, u_reveal, u_hover, u_molten, u_glint, u_time, u_spread, u_tintOn;
uniform vec4 u_geo;
uniform vec3 u_tint;
out vec4 o;
void main() {
  vec3 V = normalize(u_cam - v_w);
  if (u_facing > 0.5 && dot(v_n, V) < 0.0) discard;
  vec2 uv = v_uv;
  vec4 c;
  if (u_mode == 0) {
    c = texture(u_tex, uv);
    if (u_tintOn > 0.5 && c.a > 0.0) {
      vec3 rgb = c.rgb / c.a;
      float red = smoothstep(0.55, 0.38, max(rgb.g, rgb.b) / max(rgb.r, 0.001)) * smoothstep(0.05, 0.14, rgb.r);
      float lum = dot(rgb, vec3(0.3, 0.59, 0.11));
      rgb = mix(rgb, u_tint * (lum / 0.17), red);
      c.rgb = rgb * c.a;
    }
  } else if (u_mode == 1) {
    vec2 ctr = u_geo.xy;
    vec2 pu = (uv - ctr) / u_spread + ctr;
    vec4 a = texture(u_tex, pu);
    float rr = length(uv - ctr) / (u_geo.z * u_spread);
    float nz = texture(u_map, uv * 0.8 + 0.21).g;
    float m = 1.0 - smoothstep(u_reveal - 0.14, u_reveal, rr + (nz - 0.5) * 0.4);
    a *= m;
    vec4 b = texture(u_tex2, uv);
    c = mix(a, b, u_mix);
    if (u_molten > 0.0) {
      vec2 hp = ctr + vec2(-0.13 + 0.03 * sin(u_time * 1.9), -0.15 + 0.025 * cos(u_time * 1.4));
      float hl = exp(-dot(uv - hp, uv - hp) * 70.0);
      float hl2 = exp(-pow(length(uv - ctr) - u_geo.z * (0.55 + 0.1 * sin(u_time * 1.3)), 2.0) * 900.0) * 0.4;
      c.rgb += vec3(1.0, 0.86, 0.82) * (hl * 0.32 + hl2 * 0.12) * u_molten * c.a * (1.0 - u_mix);
    }
    if (u_hover > 0.001) {
      float px = 0.0045;
      float t0 = texture(u_txt, uv).a;
      float tl = texture(u_txt, uv - vec2(px)).a;
      float ta = texture(u_txt, uv + vec2(px)).a;
      float k = u_hover * smoothstep(0.2, 0.6, c.a) * (1.0 - u_mix);
      c.rgb *= 1.0 - t0 * 0.24 * k;
      c.rgb *= 1.0 - max(t0 - tl, 0.0) * 0.6 * k;
      c.rgb += vec3(1.0, 0.8, 0.74) * max(t0 - ta, 0.0) * 0.3 * k * c.a;
    }
    if (u_glint > 0.0 && u_glint < 1.0) {
      float s = (uv.x - ctr.x) * 0.8 + (uv.y - ctr.y) * 0.6;
      float band = exp(-pow(s - (u_glint * 1.3 - 0.65), 2.0) * 160.0);
      float lum = dot(c.rgb / max(c.a, 1e-3), vec3(0.4, 0.45, 0.15));
      c.rgb += vec3(1.0, 0.88, 0.8) * band * smoothstep(0.22, 0.5, lum) * 0.55 * c.a * u_mix;
    }
  } else {
    float x = abs(uv.x - 0.5) * 2.0;
    float a = 1.0 - smoothstep(0.5, 1.0, x);
    vec3 col = mix(u_tint * 0.6, u_tint * 1.2, 1.0 - x * x);
    col += vec3(1.0, 0.86, 0.8) * exp(-pow((uv.x - 0.36) * 9.0, 2.0)) * 0.42;
    c = vec4(col * a, a);
  }
  o = c * u_opacity;
}`;
  // Soft shadows: a polygon on the receiving plane whose edge softens with each point's height above it.
  const VS_SHADOW = HEAD + `
layout(location = 0) in vec2 a_pos;
uniform vec4 u_box; uniform float u_z;
out vec2 v_p;
void main() { vec3 w = vec3(u_box.xy + a_pos * u_box.zw, u_z); v_p = w.xy; gl_Position = project(w); }`;
  const FS_SHADOW = HEAD + `
in vec2 v_p;
uniform vec2 u_pts[16]; uniform float u_rad[16]; uniform int u_n; uniform float u_op; uniform vec3 u_col;
out vec4 o;
void main() {
  vec2 p = v_p;
  float d = dot(p - u_pts[0], p - u_pts[0]); float s = 1.0; float ws = 0.0; float rs = 0.0;
  for (int i = 0; i < 16; i++) {
    if (i >= u_n) break;
    int j = i == 0 ? u_n - 1 : i - 1;
    vec2 e = u_pts[j] - u_pts[i]; vec2 w = p - u_pts[i];
    vec2 b = w - e * clamp(dot(w, e) / dot(e, e), 0.0, 1.0);
    d = min(d, dot(b, b));
    bvec3 c = bvec3(p.y >= u_pts[i].y, p.y < u_pts[j].y, e.x * w.y > e.y * w.x);
    if (all(c) || all(not(c))) s *= -1.0;
    float wi = 1.0 / (dot(w, w) + 60.0); ws += wi; rs += wi * u_rad[i];
  }
  float sd = s * sqrt(d);
  float r = max(rs / ws, 0.6);
  float a = (1.0 - smoothstep(-r, r, sd)) * u_op;
  o = vec4(u_col * a, a);
}`;
  // What the press leaves in the page: a height map pressed in, lit by a light that slowly comes round.
  const FS_RELIEF = HEAD + `
in vec2 v_uv; in vec3 v_w; in vec3 v_n;
uniform sampler2D u_tex; uniform vec2 u_texel; uniform float u_depth, u_opacity; uniform vec3 u_lightR, u_tint;
out vec4 o;
void main() {
  vec4 t = texture(u_tex, v_uv);
  float hl = texture(u_tex, v_uv - vec2(u_texel.x, 0.0)).r, hr = texture(u_tex, v_uv + vec2(u_texel.x, 0.0)).r;
  float hu = texture(u_tex, v_uv - vec2(0.0, u_texel.y)).r, hd = texture(u_tex, v_uv + vec2(0.0, u_texel.y)).r;
  float k = u_depth * 1.5;
  vec3 N = normalize(vec3((hr - hl) * k, (hd - hu) * k, 1.0));
  vec3 L = normalize(u_lightR);
  float s = dot(N, L) / L.z;
  float dark = clamp((1.0 - s) * 0.85, 0.0, 0.3) + t.r * u_depth * 0.045;
  float light = clamp((s - 1.0) * 0.85, 0.0, 0.38);
  vec4 c = vec4(vec3(1.0, 0.99, 0.96) * light, max(dark, light));
  float ink = t.g * u_depth * 0.42;
  c = vec4(u_tint * ink, ink) + c * (1.0 - ink);
  o = c * u_opacity;
}`;
  const VS_FULL = `#version 300 es
layout(location = 0) in vec2 a_pos; out vec2 v_uv;
void main() { v_uv = a_pos; gl_Position = vec4(a_pos * 2.0 - 1.0, 0.0, 1.0); }`;
  // The cotton paper, made once: R tooth, G formation (the cloudy look of cotton), B fibres. Tiles seamlessly.
  const FS_BAKE = `#version 300 es
precision highp float;
in vec2 v_uv; uniform sampler2D u_fib; out vec4 o;
float hash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float vn(vec2 p, float per) {
  vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(mod(i, per)), hash(mod(i + vec2(1.0, 0.0), per)), u.x), mix(hash(mod(i + vec2(0.0, 1.0), per)), hash(mod(i + vec2(1.0, 1.0), per)), u.x), u.y);
}
float fbm(vec2 uv, float per, int oct) {
  float s = 0.0, a = 0.5, n = 0.0;
  for (int i = 0; i < 6; i++) { if (i >= oct) break; s += a * vn(uv * per, per); n += a; per *= 2.0; a *= 0.5; }
  return s / n;
}
void main() {
  float cloud = fbm(v_uv, 5.0, 5);
  float tooth = fbm(v_uv, 150.0, 3) * 0.72 + fbm(v_uv, 40.0, 3) * 0.28;
  float fib = texture(u_fib, v_uv).a;
  o = vec4(tooth, cloud, fib, 1.0);
}`;
  const FS_COPY = `#version 300 es
precision highp float;
in vec2 v_uv; uniform sampler2D u_tex; uniform float u_a; out vec4 o;
void main() { o = texture(u_tex, v_uv) * u_a; }`;
  // The seal stamp, revolved: depth from the view distance so it can be drawn with a depth test.
  const VS_TOOL = HEAD + `
layout(location = 0) in vec3 a_pos; layout(location = 1) in vec3 a_nrm; layout(location = 2) in vec2 a_info;
uniform mat4 u_model;
out vec3 v_w; out vec3 v_n; out vec3 v_l; out vec2 v_info;
void main() {
  vec4 w = u_model * vec4(a_pos, 1.0);
  v_w = w.xyz; v_n = normalize(mat3(u_model) * a_nrm); v_l = a_pos; v_info = a_info;
  vec4 p = project(w.xyz);
  p.z = clamp((p.w - 1300.0) / 750.0, -1.0, 1.0) * p.w;
  gl_Position = p;
}`;
  const FS_TOOL = HEAD + `
in vec3 v_w; in vec3 v_n; in vec3 v_l; in vec2 v_info;
uniform vec3 u_light; uniform float u_opacity, u_h; uniform sampler2D u_map;
out vec4 o;
// What a polished surface sees: the desk below, the room above, a large soft window to the upper left (the scene's
// light) with a hot centre, and a dimmer window to the right.
vec3 room(vec3 R, vec3 L) {
  vec3 c = mix(vec3(0.09, 0.065, 0.04), vec3(0.58, 0.52, 0.43), smoothstep(-0.3, 0.65, R.z));
  float w = dot(R, L);
  c += vec3(1.0, 0.93, 0.78) * smoothstep(0.72, 0.96, w) * 1.3;
  c += vec3(1.0, 0.97, 0.9) * pow(max(w, 0.0), 160.0) * 2.2;
  c += vec3(0.62, 0.52, 0.38) * smoothstep(0.62, 0.9, dot(R, normalize(vec3(0.75, 0.15, 0.65)))) * 0.55;
  return c;
}
void main() {
  vec3 N = normalize(v_n); vec3 V = normalize(u_cam - v_w);
  if (dot(N, V) < 0.0) N = -N;
  vec3 L = normalize(u_light);
  float ndl = max(dot(N, L), 0.0), ndv = max(dot(N, V), 0.0);
  vec3 R = reflect(-V, N);
  vec3 col;
  if (v_info.x < 0.5) {
    // antique brass: its colour times what it reflects, fine rings left by the lathe, a little patina
    float rings = 0.9 + 0.1 * sin(length(v_l.xy) / u_h * 260.0 + v_l.z / u_h * 46.0);
    float pat = texture(u_map, vec2(v_info.y * 0.21, v_l.z / u_h * 0.6)).g;
    vec3 base = vec3(0.86, 0.66, 0.36) * (0.82 + pat * 0.3);
    col = base * room(R, L) * rings * 0.95 + base * ndl * 0.08;
    col += vec3(0.95, 0.8, 0.55) * pow(1.0 - ndv, 4.0) * 0.18;
  } else {
    // ebony: brown-black with a grain running along the handle, under a clear lacquer that catches the window
    float g1 = texture(u_map, vec2(v_info.y * 0.55, v_l.z / u_h * 0.035)).r;
    float g2 = texture(u_map, vec2(v_info.y * 1.7 + 0.3, v_l.z / u_h * 0.09)).b;
    float grain = smoothstep(0.36, 0.78, g1 * 0.7 + g2 * 0.5);
    vec3 base = mix(vec3(0.075, 0.05, 0.036), vec3(0.34, 0.2, 0.115), grain);
    col = base * (0.45 + 0.95 * ndl);
    float fres = 0.05 + 0.95 * pow(1.0 - ndv, 4.0);
    float w = dot(R, L);
    col += vec3(0.9, 0.84, 0.74) * (smoothstep(-0.2, 1.0, R.z) * 0.42 * fres + smoothstep(0.55, 0.97, w) * 0.18 + pow(max(w, 0.0), 150.0) * 1.1);
    col += vec3(0.42, 0.27, 0.16) * pow(1.0 - ndv, 2.5) * 0.35;
  }
  o = vec4(col * u_opacity, u_opacity);
}`;

  const supported = () => {
    try { return !!(window.WebGL2RenderingContext && document.createElement('canvas').getContext('webgl2')); } catch (err) { return false; }
  };

  // ── one letter ───────────────────────────────────────────────────────────────────────────────
  function create(form, options) {
    const opts = options || {}, assets = opts.assets || {}, words = opts.words || {};
    const letterEl = form.querySelector('.mp-letter');
    if (!letterEl) return null;
    const canvas = document.createElement('canvas');
    canvas.className = 'mp-letter-v2 mp-rise';
    canvas.style.setProperty('--d', getComputedStyle(letterEl).getPropertyValue('--d') || '.3');
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true });
    if (!gl) return null;
    form.prepend(canvas);
    const writing = () => form.querySelector('.mp-letter__text') || form.querySelector('.mp-letter textarea');
    const stampFace = () => form.querySelector('[data-mp-stamp-toggle] .mp-stamp__paper');
    const field = name => form.elements[name];

    // GL plumbing
    function shader(type, src) {
      const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
      return s;
    }
    function program(vs, fs) {
      const p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, vs)); gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'program');
      return { p, loc: {} };
    }
    const U = (P, n) => (n in P.loc ? P.loc[n] : (P.loc[n] = gl.getUniformLocation(P.p, n)));
    let PP, PS, PSH, PR, PB, PC, PTOOL;
    try {
      PP = program(VS_PAPER, FS_PAPER); PS = program(VS_QUAD, FS_SPRITE); PSH = program(VS_SHADOW, FS_SHADOW);
      PR = program(VS_QUAD, FS_RELIEF); PB = program(VS_FULL, FS_BAKE); PC = program(VS_FULL, FS_COPY); PTOOL = program(VS_TOOL, FS_TOOL);
    } catch (err) { canvas.remove(); return null; }
    const QUAD = (() => {
      const vao = gl.createVertexArray(); gl.bindVertexArray(vao);
      const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0); gl.bindVertexArray(null);
      return vao;
    })();
    function makeMesh(pos, uv, edge, idx) {
      const vao = gl.createVertexArray(); gl.bindVertexArray(vao);
      const bufs = [[pos, 0, 2], [uv, 1, 2], [edge, 2, 1]].map(([data, loc, size]) => {
        const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0); return b;
      });
      const ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(idx), gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      return { vao, count: idx.length, bufs, ib };
    }
    function freeMesh(m) { if (!m || !m.vao) return; m.bufs.forEach(b => gl.deleteBuffer(b)); gl.deleteBuffer(m.ib); gl.deleteVertexArray(m.vao); }
    function setTex(repeat, mip) {
      if (mip) gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mip ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      const w = repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, w); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, w);
    }
    function tex(src, { repeat = false, mip = true } = {}) {
      const t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
      setTex(repeat, mip);
      return t;
    }
    function retex(t, src) { if (t) gl.deleteTexture(t); return tex(src); }

    // the paper, baked once by the shader
    let paperMap = null;
    function bakePaper() {
      const S = 1024, fib = mkCanvas(S, S), x = fib.getContext('2d');
      let seed = 7; const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
      x.lineCap = 'round';
      for (let i = 0; i < 2600; i++) {
        const px = rnd() * S, py = rnd() * S, a = rnd() * Math.PI * 2, len = 6 + rnd() * 30, bend = (rnd() - .5) * 14;
        x.strokeStyle = `rgba(255,255,255,${.05 + rnd() * .2})`; x.lineWidth = .35 + rnd() * .8;
        for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) {
          const sx = px + ox, sy = py + oy;
          if (sx < -40 || sx > S + 40 || sy < -40 || sy > S + 40) continue;
          x.beginPath(); x.moveTo(sx, sy);
          x.quadraticCurveTo(sx + Math.cos(a) * len / 2 - Math.sin(a) * bend, sy + Math.sin(a) * len / 2 + Math.cos(a) * bend, sx + Math.cos(a) * len, sy + Math.sin(a) * len);
          x.stroke();
        }
      }
      const fibTex = tex(fib, { repeat: true });
      paperMap = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, paperMap);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, S, S, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      const fb = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, paperMap, 0);
      gl.viewport(0, 0, S, S); gl.disable(gl.BLEND);
      gl.useProgram(PB.p); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fibTex); gl.uniform1i(U(PB, 'u_fib'), 0);
      gl.bindVertexArray(QUAD); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      const px = new Uint8Array(S * S * 4); gl.readPixels(0, 0, S, S, gl.RGBA, gl.UNSIGNED_BYTE, px);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(fb); gl.deleteTexture(fibTex);
      gl.bindTexture(gl.TEXTURE_2D, paperMap); setTex(true, true);
      inkMask(px, S);
    }
    // The words in the visitor's hand sit in this same paper: a mask from its tooth (the red channel) thins the ink
    // on the peaks, as a nib leaves it; the paper shader thins the folded letter's ink by the same rule (inkTooth).
    let inkURL = '';
    const INK = '.mp-letter__dear,.mp-letter__text,.mp-letter__hint,.mp-letter__yours,.mp-input';
    function inkMask(px, S) {
      const c = mkCanvas(S, S), x = c.getContext('2d'), img = x.createImageData(S, S), d = img.data;
      for (let i = 0; i < d.length; i += 4) { const t = Math.min(1, Math.max(0, (px[i] / 255 - .42) / .26)); d[i + 3] = 255 * (1 - .5 * t * t * (3 - 2 * t)); }
      x.putImageData(img, 0, 0);
      c.toBlob(b => {
        if (!b || !alive) return;
        inkURL = URL.createObjectURL(b);
        letterEl.style.setProperty('--mp-ink', `url("${inkURL}")`); inkPlace(); letterEl.classList.add('has-ink');
      });
    }
    // each word's mask starts where the paper does (the mask tiles every 512px, as the paper map does)
    function inkPlace() {
      if (!inkURL) return;
      const lr = letterEl.getBoundingClientRect();
      letterEl.querySelectorAll(INK).forEach(el => { const r = el.getBoundingClientRect(); el.style.setProperty('--ink-x', (lr.left - r.left).toFixed(1) + 'px'); el.style.setProperty('--ink-y', (lr.top - r.top).toFixed(1) + 'px'); });
    }

    // an offscreen layer, so a whole envelope can fade
    let layer = null;
    function layerOn() {
      const w = canvas.width, h = canvas.height;
      if (!layer || layer.w !== w || layer.h !== h) {
        if (layer) { gl.deleteTexture(layer.t); gl.deleteFramebuffer(layer.f); }
        const t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
        layer = { t, f, w, h };
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, layer.f); gl.viewport(0, 0, layer.w, layer.h);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    }
    function layerOff(a) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, canvas.width, canvas.height);
      if (a <= .001) return;
      gl.useProgram(PC.p); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, layer.t);
      gl.uniform1i(U(PC, 'u_tex'), 0); gl.uniform1f(U(PC, 'u_a'), a);
      gl.bindVertexArray(QUAD); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    // meshes
    function letterMesh(W, H, hA, hB) {
      const rows = Math.max(80, Math.min(240, Math.round(H / 3.5)));
      const ys = []; for (let i = 0; i <= rows; i++) ys.push(H * i / rows);
      [hA, hB].forEach(h => { if (h > 0 && h < H && !ys.some(y => Math.abs(y - h) < .01)) ys.push(h); });
      ys.sort((a, b) => a - b);
      const pos = [], uv = [], edge = [];
      ys.forEach(y => [0, W / 2, W].forEach(x => { pos.push(x, y); uv.push(x / W, y / H); edge.push(9); }));
      const mid = [], bot = [], top = [];
      for (let i = 0; i < ys.length - 1; i++) {
        const a = i * 3, b = a + 3, yc = (ys[i] + ys[i + 1]) / 2;
        (yc < hA ? top : yc > hB ? bot : mid).push(a, b, a + 1, a + 1, b, b + 1, a + 1, b + 1, a + 2, a + 2, b + 1, b + 2);
      }
      const m = makeMesh(pos, uv, edge, mid.concat(bot, top));
      m.mid = [0, mid.length]; m.bot = [mid.length, bot.length]; m.top = [mid.length + bot.length, top.length];
      return m;
    }
    function fanMesh(pts, W, H, c) {
      let dmin = 1e9; pts.forEach(p => { dmin = Math.min(dmin, Math.hypot(p[0] - c[0], p[1] - c[1])); });
      const pos = [c[0], c[1]], uv = [c[0] / W, c[1] / H], edge = [dmin], idx = [];
      pts.forEach(p => { pos.push(p[0], p[1]); uv.push(p[0] / W, p[1] / H); edge.push(0); });
      for (let i = 0; i < pts.length; i++) idx.push(0, 1 + i, 1 + (i + 1) % pts.length);
      return makeMesh(pos, uv, edge, idx);
    }
    const rectMesh = (w, h) => fanMesh([[0, 0], [w, 0], [w, h], [0, h]], w, h, [w / 2, h / 2]);
    function flapMesh(EW, FHl, rt, inset) {
      // the lining stops a hair short of the crease, so none of it shows along the fold once the flap is shut
      const f = flapShape(EW, FHl, rt), hIn = inset * f.len / FHl, end = f.tip - inset * 1.15, N = 84, u0 = inset ? 2.5 : 0;
      const pos = [], uv = [], edge = [], idx = [];
      for (let i = 0; i <= N; i++) {
        const u = u0 + (end - u0) * (1 - Math.pow(1 - i / N, 1.35)), w = Math.max(0, f.w(u) - hIn);
        [[f.half - w, 0], [f.half, w], [f.half + w, 0]].forEach(([x, e]) => { pos.push(x, -u); uv.push(x / EW, u / FHl); edge.push(e); });
      }
      for (let i = 0; i < N; i++) { const a = i * 3, b = a + 3; idx.push(a, b, a + 1, a + 1, b, b + 1, a + 1, b + 1, a + 2, a + 2, b + 1, b + 2); }
      return makeMesh(pos, uv, edge, idx);
    }
    function envelopeMeshes(EW, EH, FHl, rt) {
      const ins = Math.max(5, EW * .014);
      const left = [[0, 0], ...cubic([0, 0], [EW * .16, EH * .1], [EW * .38, EH * .32], [EW * .38, EH * .5], 14), ...cubic([EW * .38, EH * .5], [EW * .38, EH * .68], [EW * .16, EH * .9], [0, EH], 14)];
      const right = left.map(([x, y]) => [EW - x, y]);
      const bottom = [[0, EH], ...cubic([0, EH], [EW * .16, EH * .74], [EW * .34, EH * .44], [EW * .5, EH * .44], 16), ...cubic([EW * .5, EH * .44], [EW * .66, EH * .44], [EW * .84, EH * .74], [EW, EH], 16)];
      return {
        body: rectMesh(EW, EH),
        liner: fanMesh([[ins, 0], [EW - ins, 0], [EW - ins, EH - ins], [ins, EH - ins]], EW, EH, [EW / 2, EH / 2]),
        left: fanMesh(left, EW, EH, [EW * .12, EH * .5]), right: fanMesh(right, EW, EH, [EW * .88, EH * .5]),
        bottom: fanMesh(bottom, EW, EH, [EW * .5, EH * .82]),
        flap: flapMesh(EW, FHl, rt, 0), flapLiner: flapMesh(EW, FHl, rt, ins),
        outlines: { left, right, bottom }
      };
    }
    // the seal stamp: PROFILE revolved 56 times round its axis; normals smoothed except at the profile's corners
    function buildTool(h) {
      const P = PROFILE.map(([r, y, m]) => [r * h, y * h, m]), segs = 56;
      const sn = []; for (let i = 0; i < P.length - 1; i++) { const dr = P[i + 1][0] - P[i][0], dy = P[i + 1][1] - P[i][1], l = Math.hypot(dr, dy) || 1; sn.push([dy / l, -dr / l]); }
      const joins = (a, b) => a[0] * b[0] + a[1] * b[1] > .72, avg = (a, b) => { const x = a[0] + b[0], y = a[1] + b[1], l = Math.hypot(x, y) || 1; return [x / l, y / l]; };
      const pos = [], nrm = [], info = [], idx = [];
      for (let i = 0; i < P.length - 1; i++) {
        const n0 = i > 0 && joins(sn[i - 1], sn[i]) ? avg(sn[i - 1], sn[i]) : sn[i];
        const n1 = i < sn.length - 1 && joins(sn[i], sn[i + 1]) ? avg(sn[i], sn[i + 1]) : sn[i];
        const mat = P[i + 1][2], base = pos.length / 3;
        for (let k = 0; k <= segs; k++) {
          const a = k / segs * Math.PI * 2, c = Math.cos(a), s = Math.sin(a);
          [[P[i], n0], [P[i + 1], n1]].forEach(([p, n]) => { pos.push(p[0] * c, p[0] * s, p[1]); nrm.push(n[0] * c, n[0] * s, n[1]); info.push(mat, k / segs * 6.2831853); });
        }
        for (let k = 0; k < segs; k++) { const a = base + k * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
      }
      const vao = gl.createVertexArray(); gl.bindVertexArray(vao);
      const bufs = [[pos, 0, 3], [nrm, 1, 3], [info, 2, 2]].map(([d, loc, size]) => {
        const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(d), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0); return b;
      });
      const ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(idx), gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      return { vao, count: idx.length, bufs, ib };
    }

    // pictures drawn for the scene (the crest comes from the theme's mc-crest.webp)
    const IMG = {};
    // The subject stamp, as the page draws it (.mp-stamp__paper): ivory, perforated, the topic's colour, caption, crest, label.
    function stampCanvas() {
      const face = stampFace(), w = G.sw, h = G.sh, s = 3, c = mkCanvas(w * s, h * s), x = c.getContext('2d');
      const toggle = face && face.closest('[data-mp-stamp-toggle]');
      const tone = (toggle && getComputedStyle(toggle).getPropertyValue('--tone').trim()) || '#17203a';
      const caption = ((face && face.querySelector('.mp-stamp__caption')) || {}).textContent || '';
      const label = ((face && face.querySelector('.mp-stamp__label')) || {}).textContent || '';
      x.scale(s, s);
      const perf = 8, ivory = 'rgb(250,247,239)';
      x.fillStyle = ivory; x.fillRect(0, 0, w, h);
      x.globalCompositeOperation = 'destination-out'; x.beginPath();
      for (let px = 0; px <= w + .1; px += perf) { x.moveTo(px + 2.4, 0); x.arc(px, 0, 2.4, 0, 7); x.moveTo(px + 2.4, h); x.arc(px, h, 2.4, 0, 7); }
      for (let py = 0; py <= h + .1; py += perf) { x.moveTo(2.4, py); x.arc(0, py, 2.4, 0, 7); x.moveTo(w + 2.4, py); x.arc(w, py, 2.4, 0, 7); }
      x.fill(); x.globalCompositeOperation = 'source-over';
      const m = perf * .9, aw = w - m * 2, ah = h - m * 2;
      x.fillStyle = tone; x.fillRect(m, m, aw, ah);
      const g = x.createRadialGradient(m + aw / 2, m + ah * .32, 0, m + aw / 2, m + ah * .32, aw * .7);
      g.addColorStop(0, 'rgba(255,255,255,.16)'); g.addColorStop(1, 'rgba(255,255,255,0)');
      x.fillStyle = g; x.fillRect(m, m, aw, ah);
      x.strokeStyle = 'rgba(250,247,239,.34)'; x.lineWidth = 1; x.strokeRect(m + 3.5, m + 3.5, aw - 7, ah - 7);
      x.fillStyle = 'rgba(250,247,239,.78)'; x.textAlign = 'center';
      if (caption) { x.font = '600 7px "DM Sans"'; try { x.letterSpacing = '2.1px'; } catch (e) { /* older canvases */ } x.fillText(caption.toUpperCase(), w / 2 + 1, m + 17); try { x.letterSpacing = '0px'; } catch (e) { /* */ } }
      const cw = aw * .3, ch = cw * 857 / 640;
      x.globalAlpha = .94; x.drawImage(tinted(IMG.crest, cw * s, ch * s, '#faf7ef'), (w - cw) / 2, m + ah * .5 - ch * .55, cw, ch); x.globalAlpha = 1;
      let fs = 17; x.font = `italic 400 ${fs}px "Cormorant Garamond"`;
      while (x.measureText(label).width > aw - 12 && fs > 9) { fs -= .5; x.font = `italic 400 ${fs}px "Cormorant Garamond"`; }
      x.fillStyle = '#faf7ef'; x.fillText(label, w / 2, h - m - 12);
      return c;
    }
    function sendMask(w) {
      const S = 512, c = mkCanvas(S, S), x = c.getContext('2d');
      x.fillStyle = '#fff'; x.textAlign = 'center'; x.textBaseline = 'middle';
      x.font = `italic 400 ${Math.round(w.well * S * .62)}px "Cormorant Garamond"`;
      x.fillText(words.seal || 'Send', w.wx * S, w.wy * S + 4);
      return c;
    }
    // The letter's words, drawn where the page laid them out, so the paper can take over without a jump. Left out:
    // the stamp (lifted off by itself), the send button and its note, hints and messages, hidden text.
    const SKIP = '.mp-stamp,.mp-letter__send,.mp-letter__hint,.mp-letter__empty,.mp-status,.mp-sr,[hidden],textarea,script,style';
    function captureInk(scale) {
      const r0 = letterEl.getBoundingClientRect(), c = mkCanvas(r0.width * scale, r0.height * scale), x = c.getContext('2d');
      x.scale(scale, scale); x.textBaseline = 'alphabetic';
      const range = document.createRange(), walk = document.createTreeWalker(letterEl, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walk.nextNode())) {
        const el = node.parentElement; if (!el || el.closest(SKIP)) continue;
        const cs = getComputedStyle(el); if (cs.visibility === 'hidden' || +cs.opacity === 0) continue;
        x.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        try { x.letterSpacing = cs.letterSpacing === 'normal' ? '0px' : cs.letterSpacing; } catch (e) { /* older canvases */ }
        const asc = x.measureText('Hg').fontBoundingBoxAscent, up = cs.textTransform === 'uppercase';
        x.fillStyle = `rgba(255,255,255,${inkAlpha(cs.color)})`;
        const re = /\S+/g; let mt;
        while ((mt = re.exec(node.data))) {
          range.setStart(node, mt.index); range.setEnd(node, mt.index + mt[0].length);
          const rects = range.getClientRects();
          if (rects.length === 1) { const rc = rects[0]; x.fillText(up ? mt[0].toUpperCase() : mt[0], rc.left - r0.left, rc.top - r0.top + asc); continue; }
          for (let i = 0; i < mt[0].length; i++) {
            range.setStart(node, mt.index + i); range.setEnd(node, mt.index + i + 1);
            const rc = range.getBoundingClientRect(), ch = mt[0][i];
            x.fillText(up ? ch.toUpperCase() : ch, rc.left - r0.left, rc.top - r0.top + asc);
          }
        }
      }
      letterEl.querySelectorAll('.mp-input').forEach(inp => {
        if (!inp.value) return;
        const cs = getComputedStyle(inp), rc = inp.getBoundingClientRect();
        x.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        try { x.letterSpacing = '0px'; } catch (e) { /* */ }
        const mm = x.measureText(inp.value), asc = mm.fontBoundingBoxAscent, desc = mm.fontBoundingBoxDescent;
        const top = rc.top + parseFloat(cs.borderTopWidth) + parseFloat(cs.paddingTop);
        const hgt = rc.height - parseFloat(cs.borderTopWidth) - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - parseFloat(cs.borderBottomWidth);
        x.fillStyle = `rgba(255,255,255,${inkAlpha(cs.color)})`;
        x.save(); x.beginPath(); x.rect(rc.left - r0.left, rc.top - r0.top, rc.width, rc.height); x.clip();
        x.fillText(inp.value, rc.left - r0.left + parseFloat(cs.paddingLeft), top + (hgt - asc - desc) / 2 + asc - r0.top);
        x.restore();
      });
      return c;
    }
    // The stationery, measured from the page (channels add up): red, bronze foil (a double frame with notched corners and
    // a star in each, the crest letterhead); green, writing lines under each line of the message (stopping short of the
    // stamp) and under name and email; blue, a crest watermark in the paper.
    function printCanvas() {
      const r0 = letterEl.getBoundingClientRect(), W = r0.width, H = r0.height, s = 2;
      const c = mkCanvas(W * s, H * s), x = c.getContext('2d');
      x.scale(s, s); x.globalCompositeOperation = 'lighter';
      const narrow = W < 460, o = narrow ? 10 : 13, i = o + (narrow ? 4 : 5), n = narrow ? 6.5 : 8.5;
      x.strokeStyle = '#f00'; x.fillStyle = '#f00';
      x.lineWidth = 1.15; x.strokeRect(o, o, W - o * 2, H - o * 2);
      x.lineWidth = .6; x.beginPath();
      x.moveTo(i + n, i); x.lineTo(W - i - n, i); x.arc(W - i, i, n, Math.PI, Math.PI / 2, true);
      x.lineTo(W - i, H - i - n); x.arc(W - i, H - i, n, -Math.PI / 2, -Math.PI, true);
      x.lineTo(i + n, H - i); x.arc(i, H - i, n, 0, -Math.PI / 2, true);
      x.lineTo(i, i + n); x.arc(i, i, n, Math.PI / 2, 0, true);
      x.closePath(); x.stroke();
      const star = (cx, cy, r) => {
        x.beginPath();
        for (let k = 0; k < 16; k++) { const a = k * Math.PI / 8 - Math.PI / 2, rr = k % 2 ? r * .42 : r; x.lineTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr); }
        x.closePath(); x.fill();
      };
      [[i, i], [W - i, i], [W - i, H - i], [i, H - i]].forEach(([a, b]) => star(a, b, narrow ? 3 : 3.6));
      const ch = narrow ? 30 : 38, cw = ch * 640 / 857, top = narrow ? 20 : 24;
      x.drawImage(tinted(IMG.crest, cw * s, ch * s, '#f00'), (W - cw) / 2, top, cw, ch);
      const ws = narrow ? 8 : 9.2, sp = ws * .52, house = (words.house || 'MENASA').toUpperCase();
      x.font = `500 ${ws}px "DM Sans"`; x.textAlign = 'center';
      try { x.letterSpacing = sp + 'px'; } catch (e) { /* */ }
      const wy = top + ch + (narrow ? 11 : 13), tw = x.measureText(house).width;
      x.fillText(house, W / 2 + sp / 2, wy);
      try { x.letterSpacing = '0px'; } catch (e) { /* */ }
      const hl = narrow ? 18 : 26;
      x.fillRect(W / 2 - tw / 2 - 10 - hl, wy - ws * .36, hl, .6); x.fillRect(W / 2 + tw / 2 + 10, wy - ws * .36, hl, .6);
      star(W / 2 - tw / 2 - 14 - hl, wy - ws * .36 + .3, 2.2); star(W / 2 + tw / 2 + 14 + hl, wy - ws * .36 + .3, 2.2);
      const body = writing();
      if (body) {
        const bs = getComputedStyle(body), br = body.getBoundingClientRect();
        const lh = parseFloat(bs.lineHeight) || parseFloat(bs.fontSize) * 1.75, fs = parseFloat(bs.fontSize);
        x.font = `${bs.fontStyle} ${bs.fontWeight} ${bs.fontSize} ${bs.fontFamily}`;
        const mm = x.measureText('Hg'), asc = mm.fontBoundingBoxAscent, desc = mm.fontBoundingBoxDescent, half = (lh - asc - desc) / 2;
        const fl = letterEl.querySelector('.mp-stamp');
        let floatBottom = -1e9, floatLeft = 1e9;
        if (fl) { const sr = fl.getBoundingClientRect(), sm = getComputedStyle(fl); floatBottom = sr.bottom + parseFloat(sm.marginBottom) - r0.top; floatLeft = sr.left - parseFloat(sm.marginLeft) - r0.left; }
        const left = br.left - r0.left, right = br.right - r0.left;
        x.fillStyle = '#0f0';
        for (let lineTop = br.top - r0.top; lineTop + lh <= br.bottom - r0.top + 1; lineTop += lh) {
          const y = lineTop + half + asc + fs * .14, end = lineTop < floatBottom ? Math.min(right, floatLeft + 6) : right;
          x.fillRect(left, y, end - left, .75);
        }
      }
      x.fillStyle = '#0f0';
      letterEl.querySelectorAll('.mp-input').forEach(inp => { const rc = inp.getBoundingClientRect(); x.fillRect(rc.left - r0.left, rc.bottom - r0.top - 1, rc.width, .75); });
      try { x.filter = 'blur(1.6px)'; } catch (e) { /* */ }
      const mh = Math.min(H * .32, W * .46), mw = mh * 640 / 857;
      x.drawImage(tinted(IMG.crest, mw * s, mh * s, '#00f'), (W - mw) / 2, (H - mh) / 2 + H * .05, mw, mh);
      return c;
    }
    // The envelope's face, seen from the front: printed in bronze foil (red channel).
    function addressCanvas() {
      const { EW, EH } = G, s = 2, c = mkCanvas(EW * s, EH * s), x = c.getContext('2d');
      x.scale(s, s); x.fillStyle = '#f00'; x.strokeStyle = '#f00'; x.textAlign = 'center';
      x.lineWidth = .7; x.strokeRect(9.5, 9.5, EW - 19, EH - 19);
      x.lineWidth = .4; x.strokeRect(12.5, 12.5, EW - 25, EH - 25);
      const ch = EH * .15, cw = ch * 640 / 857;
      x.drawImage(tinted(IMG.crest, cw * s, ch * s, '#f00'), 24, 22, cw, ch);
      const cx = EW * .5, fs = Math.max(13, EH * .058);
      x.font = `italic 400 ${fs}px "Cormorant Garamond"`; x.fillText(words.to || 'To the House of', cx, EH * .55);
      const big = Math.max(18, EH * .1);
      x.font = `500 ${big}px "Cormorant Garamond"`; try { x.letterSpacing = (big * .3) + 'px'; } catch (e) { /* */ }
      x.fillText((words.house || 'MENASA').toUpperCase(), cx + big * .15, EH * .55 + big * 1.12);
      try { x.letterSpacing = '0px'; } catch (e) { /* */ }
      x.fillRect(cx - EW * .08, EH * .55 + big * 1.55, EW * .16, .7);
      return c;
    }
    // The writing on the closed flap: the sender's name and email in ink (green), a short foil rule between (red).
    function backCanvas() {
      const { EW } = G, d = G.decal, s = 2, c = mkCanvas(d.w * s, d.h * s), x = c.getContext('2d');
      x.scale(s, s); x.textAlign = 'center';
      const nameV = ((field('contact[name]') || {}).value || '').trim(), mail = ((field('contact[email]') || {}).value || '').trim();
      const name = nameV || mail, sub = nameV ? mail : '';
      const hand = handReady ? '"Pinyon Script"' : '"Cormorant Garamond"', handStyle = handReady ? '400' : 'italic 400';
      const avail = y => { const w = G.flapW(d.y + y); return Math.min(d.w, w * 2 - 26); };
      const nb = d.h * (sub ? .5 : .66), eb = d.h * .9;
      let ns = Math.min(d.h * .42, EW * .062); x.font = `${handStyle} ${ns}px ${hand}`;
      while (x.measureText(name).width > avail(nb - ns * .4) * .94 && ns > 12) { ns -= .5; x.font = `${handStyle} ${ns}px ${hand}`; }
      x.fillStyle = '#0f0'; x.fillText(name, d.w / 2, nb);
      const nw = x.measureText(name).width;
      let es = Math.min(d.h * .2, EW * .03, 17), mw = 0;
      if (sub) {
        x.font = `italic 400 ${es}px "Cormorant Garamond"`;
        while (x.measureText(sub).width > avail(eb) * .94 && es > 9) { es -= .5; x.font = `italic 400 ${es}px "Cormorant Garamond"`; }
        x.fillText(sub, d.w / 2, eb); mw = x.measureText(sub).width;
      }
      x.fillStyle = '#f00'; const ry = sub ? nb + (eb - es - nb) * .5 : nb + d.h * .14;
      x.fillRect(d.w / 2 - d.w * .1, ry, d.w * .2, .6);
      x.beginPath(); x.moveTo(d.w / 2, ry - 2.4); x.lineTo(d.w / 2 + 2.4, ry + .3); x.lineTo(d.w / 2, ry + 3); x.lineTo(d.w / 2 - 2.4, ry + .3); x.fill();
      d.split = (ry + .3) / d.h;
      d.lines = [(d.w - nw) / 2 / d.w, (d.w + nw) / 2 / d.w, (d.w - mw) / 2 / d.w, (d.w + mw) / 2 / d.w];
      return c;
    }
    function postmarkCanvas() {
      const w = 168, h = 86, s = 2, c = mkCanvas(w * s, h * s), x = c.getContext('2d');
      x.scale(s, s); x.strokeStyle = '#fff'; x.fillStyle = '#fff';
      const cx = 124, cy = 43, r = 33;
      x.lineWidth = 1.5; x.beginPath(); x.arc(cx, cy, r, 0, 7); x.stroke();
      x.lineWidth = .8; x.beginPath(); x.arc(cx, cy, r - 4.5, 0, 7); x.stroke();
      const arcText = (txt, rad, start, dirn) => {
        x.font = '500 7.2px "DM Sans"';
        const wid = [...txt].map(ch => x.measureText(ch).width + 1.6), tot = wid.reduce((a, b) => a + b, 0);
        let a = start - dirn * tot / rad / 2;
        [...txt].forEach((ch, i) => {
          const step = wid[i] / rad; a += dirn * step / 2;
          x.save(); x.translate(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad); x.rotate(a + dirn * Math.PI / 2);
          x.textAlign = 'center'; x.fillText(ch, 0, 2.6); x.restore(); a += dirn * step / 2;
        });
      };
      arcText((words.house || 'MENASA').toUpperCase(), r - 10.5, -Math.PI / 2, 1);
      arcText('THE HOUSE', r - 10.5, Math.PI / 2, -1);
      const d = new Date();
      x.textAlign = 'center'; x.font = '500 8.4px "DM Sans"';
      x.fillText(d.getDate() + ' ' + d.toLocaleString('en-GB', { month: 'short' }).toUpperCase(), cx, cy - .5);
      x.font = '500 7.4px "DM Sans"'; x.fillText(String(d.getFullYear()), cx, cy + 9);
      x.lineWidth = 1.4;
      for (let k = 0; k < 4; k++) {
        const y = 26 + k * 11; x.beginPath();
        for (let px = 2; px <= 94; px += 2) { const yy = y + Math.sin(px / 7.5 + k * .8) * 2.6; if (px === 2) x.moveTo(px, yy); else x.lineTo(px, yy); }
        x.stroke();
      }
      x.globalCompositeOperation = 'destination-out';
      let seed = 11; const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
      for (let i = 0; i < 420; i++) { x.globalAlpha = .25 + rnd() * .7; x.beginPath(); x.arc(rnd() * w, rnd() * h, .3 + rnd() * 1.1, 0, 7); x.fill(); }
      return c;
    }
    // What the press leaves in the page: the crest and two lines, debossed (R height, G the bronze-tinted words).
    function reliefCanvas() {
      const R = G.relief, s = 2, W = R.w * s, H = R.h * s;
      const hc = mkCanvas(W, H), hx = hc.getContext('2d'), tc = mkCanvas(W, H), tx = tc.getContext('2d');
      const ch = R.h * .46, cw = ch * 640 / 857;
      try { hx.filter = 'blur(2.6px)'; } catch (e) { /* */ }
      hx.drawImage(tinted(IMG.crest, cw * s, ch * s, '#fff'), (W - cw * s) / 2, R.h * .02 * s, cw * s, ch * s);
      const lines = x => {
        x.fillStyle = '#fff'; x.textAlign = 'center';
        const f1 = Math.max(10.5, R.w * .034), line = (words.engraved || 'Received by the house').toUpperCase();
        x.font = `600 ${f1 * s}px "Cormorant Garamond"`; try { x.letterSpacing = (f1 * .32 * s) + 'px'; } catch (e) { /* */ }
        let f = f1; while (x.measureText(line).width > W * .96 && f > 8) { f -= .5; x.font = `600 ${f * s}px "Cormorant Garamond"`; try { x.letterSpacing = (f * .32 * s) + 'px'; } catch (e) { /* */ } }
        x.fillText(line, W / 2 + f * .16 * s, R.h * .66 * s);
        try { x.letterSpacing = '0px'; } catch (e) { /* */ }
        const d = new Date(), f2 = Math.max(13, R.w * .047);
        const when = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        x.font = `italic 400 ${f2 * s}px "Cormorant Garamond"`; x.fillText(when, W / 2, R.h * .81 * s);
      };
      try { hx.filter = 'blur(1.5px)'; } catch (e) { /* */ }
      lines(hx); lines(tx);
      const a = hx.getImageData(0, 0, W, H).data, b = tx.getImageData(0, 0, W, H).data, out = hx.createImageData(W, H);
      for (let i = 0; i < a.length; i += 4) { out.data[i] = a[i + 3]; out.data[i + 1] = b[i + 3]; out.data[i + 3] = 255; }
      const c = mkCanvas(W, H); c.getContext('2d').putImageData(out, 0, 0);
      return c;
    }

    // layout, measured from the page (the scene's origin is the form's top-left corner)
    let G = null, PT = null, CAM = null;
    const mesh = { letter: null, env: null, stamp: null, decal: null, pm: null, tool: null };
    const T = { ink: null, stamp: null, addr: null, back: null, pm: null, relief: null, print: null, blank: null, send: null, spoon: null, pool: null, seal: null };
    let toolH = 0, handReady = false;
    function sizeCanvas(extra) {
      const fr = form.getBoundingClientRect(), vw = document.documentElement.clientWidth;
      const CW = vw, CH = Math.ceil(Math.max(fr.height, extra || 0) + BLEED * 2);
      let dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cap = 4.4e6; if (CW * CH * dpr * dpr > cap) dpr = Math.sqrt(cap / (CW * CH));
      Object.assign(canvas.style, { left: -fr.left + 'px', top: -BLEED + 'px', width: CW + 'px', height: CH + 'px' });
      const bw = Math.round(CW * dpr), bh = Math.round(CH * dpr);
      if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
      CAM = { x: -fr.left + CW / 2, y: -BLEED + CH / 2, P: PERSP, hw: CW / 2, hh: CH / 2 };
    }
    function measure() {
      const fr = form.getBoundingClientRect(), lr = letterEl.getBoundingClientRect(), face = stampFace();
      const vw = document.documentElement.clientWidth;
      // where the letter rests, without its rise-in (the canvas rises with it, so the two always meet)
      const tf = getComputedStyle(letterEl).transform, t = tf && tf !== 'none' ? new DOMMatrixReadOnly(tf) : null, tx = t ? t.m41 : 0, ty = t ? t.m42 : 0;
      const W = lr.width, H = lr.height, lx = lr.left - fr.left - tx, ly = lr.top - fr.top - ty;
      const thirds = H > W * 1.45, fh = thirds ? H / 3 : H / 2;
      const g = { W, H, lx, ly, thirds, fh, hA: thirds ? H / 3 : H / 2, hB: thirds ? H * 2 / 3 : H, pivotY: thirds ? H / 2 : H * .75 };
      g.EW = W + Math.max(16, W * .045); g.EH = fh + Math.max(26, fh * .13);
      g.es = Math.min(1, (vw - 20) / g.EW); g.EWs = g.EW * g.es; g.EHs = g.EH * g.es;
      // the envelope sits just low enough for the folded letter to wait above its opening, in the letter's own space
      g.ex = lx + W / 2; g.ey = ly + fh * g.es + 8 + g.EHs / 2;
      g.FHl = g.EH * .68; g.rt = g.EW * .09;
      const fs = flapShape(g.EW, g.FHl, g.rt); g.tip = fs.tip; g.flapW = fs.w;
      g.D = Math.min(g.EW * .25, g.EH * .44);
      g.die = g.D * .64;
      g.sealY = g.tip - g.D * .28;
      g.envTop = g.ey - g.EHs / 2;
      g.hoverY = g.envTop + 2 - fh * g.es / 2;
      if (face) { const sr = face.getBoundingClientRect(); g.sw = sr.width; g.sh = sr.height; g.sx = sr.left - fr.left - tx + g.sw / 2; g.sy = sr.top - fr.top - ty + g.sh / 2; }
      else { g.sw = 0; g.sh = 0; }
      const top = g.EH * .07, bottom = g.sealY - g.D * .5 - 8;
      g.decal = { x: g.EW * .17, y: top, w: g.EW * .66, h: Math.max(30, bottom - top) };
      g.stampOn = { w: .86, fx: g.EW - 18 - g.sw * .43, fy: 18 + g.sh * .43 };
      g.relief = { w: Math.min(g.EWs * .8, 380) };
      g.relief.h = g.relief.w * .62;
      return g;
    }
    function layoutIdle() {
      sizeCanvas();
      const g = measure(), old = G; G = g;
      if (!old || old.W !== g.W || old.H !== g.H) { freeMesh(mesh.letter); mesh.letter = letterMesh(g.W, g.H, g.hA, g.hB); }
      T.print = retex(T.print, printCanvas());
      inkPlace();
    }
    // Each letter one of the six waxes, never the same twice running in this tab.
    let waxIdx = 0, pick = -1;
    const nextWax = () => {
      let lastIdx = -1;
      try { lastIdx = +sessionStorage.getItem('menasa:wax'); } catch (e) { /* */ }
      const pool = WAX.map((_, i) => i).filter(i => i !== lastIdx);
      return pool[Math.floor(Math.random() * pool.length)];
    };
    // The pictures and the hand are fetched once the visitor starts on the letter (or presses send), not with the page.
    let fetched = null;
    function prefetch() {
      if (fetched) return fetched;
      pick = nextWax();
      const pair = (assets.wax || [])[pick] || [];
      const font = assets.hand && window.FontFace ? new FontFace('Pinyon Script', `url(${assets.hand})`).load().then(f => { document.fonts.add(f); handReady = true; }).catch(() => {}) : Promise.resolve();
      fetched = Promise.all([loadImg(pair[0]), loadImg(pair[1]), loadImg(assets.spoon), font]);
      return fetched;
    }
    async function buildSend() {
      const [pool, seal, spoon] = await prefetch();
      if (!alive) return false;
      waxIdx = pick;
      try { sessionStorage.setItem('menasa:wax', String(waxIdx)); } catch (e) { /* */ }
      const fr = form.getBoundingClientRect();
      sizeCanvas(fr.height + letterEl.offsetHeight * .6 + 300);
      G = measure(); PT = timeline(G.thirds ? .3 : 0);
      freeMesh(mesh.letter); mesh.letter = letterMesh(G.W, G.H, G.hA, G.hB);
      if (mesh.env) Object.values(mesh.env).forEach(freeMesh);
      mesh.env = envelopeMeshes(G.EW, G.EH, G.FHl, G.rt);
      freeMesh(mesh.decal); mesh.decal = rectMesh(G.decal.w, G.decal.h);
      freeMesh(mesh.pm); mesh.pm = rectMesh(168, 86);
      freeMesh(mesh.stamp); mesh.stamp = G.sw ? rectMesh(G.sw, G.sh) : null;
      const scale = Math.min(2.5, Math.max(2, window.devicePixelRatio || 1));
      T.ink = retex(T.ink, captureInk(scale));
      T.print = retex(T.print, printCanvas());
      if (mesh.stamp) T.stamp = retex(T.stamp, stampCanvas());
      T.addr = retex(T.addr, addressCanvas());
      T.back = retex(T.back, backCanvas());
      T.pm = retex(T.pm, postmarkCanvas());
      T.relief = retex(T.relief, reliefCanvas());
      T.send = retex(T.send, sendMask(WAX[waxIdx]));
      T.pool = retex(T.pool, pool || mkCanvas(1, 1)); T.seal = retex(T.seal, seal || mkCanvas(1, 1));
      if (!T.spoon) T.spoon = tex(spoon || mkCanvas(1, 1));
      return true;
    }

    // drawing
    const Z4 = [0, 0, 1, 0];
    // The eye stays over the envelope, as in the first test page: the form below it can be far taller on a phone, and
    // an eye at the canvas centre saw the stamp from the side, standing above the seal instead of on it.
    function camera(P) {
      const ex = G ? G.ex : CAM.x, ey = G ? G.ey + (G.sealY - G.EH / 2 + G.D * .5) * G.es : CAM.y;
      gl.uniform3f(U(P, 'u_cam'), ex, ey, CAM.P); gl.uniform2f(U(P, 'u_half'), CAM.hw, CAM.hh); gl.uniform2f(U(P, 'u_off'), ex - CAM.x, ey - CAM.y);
    }
    function drawPaper(m, model, o, range) {
      const P = PP; gl.useProgram(P.p); camera(P);
      gl.uniformMatrix4fv(U(P, 'u_model'), false, model);
      gl.uniform4fv(U(P, 'u_foldA'), o.foldA || Z4); gl.uniform4fv(U(P, 'u_foldB'), o.foldB || Z4);
      gl.uniform1f(U(P, 'u_lift'), o.lift || 0);
      gl.uniform1i(U(P, 'u_matF'), o.matF ?? 1); gl.uniform1i(U(P, 'u_matB'), o.matB ?? 1);
      gl.uniform3fv(U(P, 'u_colF'), o.colF || COL.letter); gl.uniform3fv(U(P, 'u_colB'), o.colB || o.colF || COL.letter);
      gl.uniform1i(U(P, 'u_inkF'), o.inkF || 0); gl.uniform1i(U(P, 'u_inkB'), o.inkB || 0);
      gl.uniform3fv(U(P, 'u_inkCol'), o.inkCol || COL.ink);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, paperMap); gl.uniform1i(U(P, 'u_map'), 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, o.ink || T.blank); gl.uniform1i(U(P, 'u_ink'), 1);
      gl.uniform2fv(U(P, 'u_inkTexel'), o.texel || [.0015, .0015]);
      gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, o.print || T.blank); gl.uniform1i(U(P, 'u_print'), 2);
      gl.uniform1f(U(P, 'u_printOn'), o.print ? 1 : 0); gl.uniform3fv(U(P, 'u_ruleCol'), COL.rule);
      gl.uniform2fv(U(P, 'u_printTexel'), o.print && G ? [.6 / G.W, .6 / G.H] : [.001, .001]);
      gl.uniform4fv(U(P, 'u_reveal'), o.reveal || [2, 2, .5, 0]);
      gl.uniform1f(U(P, 'u_wet'), o.wet || 0);
      gl.uniform2fv(U(P, 'u_size'), o.size || [1, 1]); gl.uniform1f(U(P, 'u_deckle'), o.deckle || 0);
      gl.uniform1f(U(P, 'u_alpha'), o.alpha ?? 1); gl.uniform1f(U(P, 'u_rim'), o.rim || 0); gl.uniform1f(U(P, 'u_alphaTex'), o.alphaTex ? 1 : 0);
      gl.uniform1f(U(P, 'u_bump'), o.bump ?? 1); gl.uniform1f(U(P, 'u_mapScale'), 512); gl.uniform1f(U(P, 'u_linerScale'), 30);
      gl.uniform3fv(U(P, 'u_light'), LIGHT);
      gl.bindVertexArray(m.vao);
      const [first, count] = range || [0, m.count];
      if (count > 0) gl.drawElements(gl.TRIANGLES, count, gl.UNSIGNED_INT, first * 4);
    }
    function drawSprite(model, o) {
      const P = PS; gl.useProgram(P.p); camera(P);
      gl.uniformMatrix4fv(U(P, 'u_model'), false, model);
      gl.uniform1i(U(P, 'u_mode'), o.mode || 0);
      gl.uniform1f(U(P, 'u_opacity'), o.alpha ?? 1); gl.uniform1f(U(P, 'u_facing'), o.facing ? 1 : 0);
      gl.uniform1f(U(P, 'u_mix'), o.mix || 0); gl.uniform1f(U(P, 'u_reveal'), o.reveal ?? 2); gl.uniform1f(U(P, 'u_hover'), o.hover || 0);
      gl.uniform1f(U(P, 'u_molten'), o.molten || 0); gl.uniform1f(U(P, 'u_glint'), o.glint || 0); gl.uniform1f(U(P, 'u_time'), clock);
      gl.uniform1f(U(P, 'u_spread'), o.spread || 1);
      gl.uniform4fv(U(P, 'u_geo'), o.geo || [.5, .5, .4, 0]);
      gl.uniform1f(U(P, 'u_tintOn'), o.tint ? 1 : 0); gl.uniform3fv(U(P, 'u_tint'), hex(WAX[waxIdx].tint));
      [[o.tex, 'u_tex'], [o.tex2 || T.blank, 'u_tex2'], [T.send, 'u_txt'], [paperMap, 'u_map']].forEach(([t, n], i) => {
        gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, t || T.blank); gl.uniform1i(U(P, n), i);
      });
      gl.bindVertexArray(QUAD); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    function drawShadowPoly(pts, z, op) {
      if (pts.length < 3 || op < .004) return;
      let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9, rm = 0;
      pts.forEach(p => { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); rm = Math.max(rm, p.r); });
      const pad = rm * 1.3 + 2;
      const P = PSH; gl.useProgram(P.p); camera(P);
      gl.uniform4f(U(P, 'u_box'), x0 - pad, y0 - pad, x1 - x0 + pad * 2, y1 - y0 + pad * 2); gl.uniform1f(U(P, 'u_z'), z);
      const flat = new Float32Array(32), rad = new Float32Array(16);
      pts.forEach((p, i) => { flat[i * 2] = p.x; flat[i * 2 + 1] = p.y; rad[i] = p.r; });
      gl.uniform2fv(U(P, 'u_pts'), flat); gl.uniform1fv(U(P, 'u_rad'), rad); gl.uniform1i(U(P, 'u_n'), pts.length);
      gl.uniform1f(U(P, 'u_op'), op); gl.uniform3fv(U(P, 'u_col'), COL.shadow);
      gl.bindVertexArray(QUAD); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    // Two layers: a tight contact shadow and a soft drop shadow, both softer and lighter with height.
    const SHD = [.2, .3];
    function castShadow(pts3, zr, strength = 1, lift = 0) {
      if (!pts3.length) return;
      let hs = 0; pts3.forEach(p => { hs += Math.max(0, p[2] - zr + lift); }); const hAvg = hs / pts3.length;
      const layers = [{ k: .32, r0: .7, rk: .035, op: .22 * Math.exp(-hAvg / 22) }, { k: 1, r0: 2.4, rk: .12, op: .13 / (1 + hAvg / 240) }];
      for (const L of layers) {
        const pts = pts3.map(p => { const h = Math.max(0, p[2] - zr + lift); return { x: p[0] + (h * SHD[0] + 1.2) * L.k, y: p[1] + (h * SHD[1] + 2) * L.k, r: L.r0 + h * L.rk }; });
        drawShadowPoly(hull(pts), zr, L.op * strength);
      }
    }
    const corners = (m, w, h, z = 0) => [[0, 0], [w, 0], [w, h], [0, h]].map(([x, y]) => M4.apply(m, x, y, z));
    function circlePts(cx, cy, z, r, n = 12) { const o = []; for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; o.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r, z]); } return o; }
    function panelPts(m, f, dir) {
      const out = [], h = f[0], len = dir < 0 ? h : G.H - h;
      for (const s of [0, .34, .67, 1]) for (const x of [0, G.W]) { const b = bendPt(x, dir < 0 ? h - len * s : h + len * s, f, dir); out.push(M4.apply(m, b[0], b[1], b[2])); }
      return out;
    }

    // the scene at a given time
    let mode = 'idle', tp = 0, ts = -1, hover = 0, hoverTarget = 0, clock = 0, last = 0, raf = 0, alive = true, pressed = false;
    const waiters = [];
    const waitFor = test => new Promise(res => { waiters.push({ test, res }); kick(); });
    function pose() {
      const g = G, P = PT, t = tp, q = ts, S = {};
      let thA = Math.PI * E.io(seg(t, ...P.foldA)), thB = g.thirds ? Math.PI * E.io(seg(t, ...P.foldB)) : 0;
      const open = .075 * E.out(seg(t, P.settle, P.settle + .4)) * (1 - E.io(seg(t, ...P.flatten)));
      thA = Math.min(thA, Math.PI - open); if (g.thirds) thB = Math.min(thB, Math.PI - open * .5);
      const rad = th => .7 + 26 * (1 - E.io(seg(th, Math.PI * .4, Math.PI * .97)));
      S.foldA = [g.hA, thA, rad(thA), .55 * Math.sin(thA) / g.fh];
      S.foldB = g.thirds ? [g.hB, thB, rad(thB), .55 * Math.sin(thB) / g.fh] : [g.H, 0, 1, 0];
      const lift = seg(t, ...P.lift), down = seg(t, ...P.down), ins = seg(t, ...P.insert);
      let lx = lerp(g.lx + g.W / 2, g.ex, E.io(lift)), ly = lerp(g.ly + g.pivotY, g.hoverY, E.io(lift));
      ly = lerp(ly, g.ey, E.io(ins));
      const lz = 36 * Math.sin(Math.PI / 2 * E.io(lift)) * (1 - E.io(down)) + .4 * E.io(down);
      const lsc = lerp(1, g.es, E.io(lift)), lrz = -.028 * Math.sin(Math.PI * lift) - .012 * Math.sin(Math.PI * ins);
      S.letter = { m: chain(M4.T(lx, ly, lz), M4.RZ(lrz), M4.S(lsc), M4.T(-g.W / 2, -g.pivotY)), z: lz, inside: down > .5, show: q < SP.lift[0] };
      const ein = seg(t, ...P.envIn);
      let ex = g.ex, ey = g.ey + 190 * (1 - E.out5(ein)), ez = 0, erx = 0, ery = 0, erz = .05 * (1 - E.out5(ein));
      let eo = E.out(seg(t, P.envIn[0], P.envIn[0] + .35));
      let fth = Math.PI * E.io(seg(t, ...P.flap));
      const bn = seg(t, ...P.bounce); fth -= .08 * Math.sin(bn * Math.PI) * (1 - bn);
      S.flap = [0, fth, .6 + 12 * (1 - E.io(seg(fth, Math.PI * .4, Math.PI * .97))), .4 * Math.sin(fth) / g.FHl];
      S.envShow = t >= P.envIn[0];
      const dl = g.decal.lines || [0, 1, 0, 1];
      S.reveal = [lerp(dl[0] - .02, dl[1] + .03, E.sine(seg(t, ...P.name))), lerp(dl[2] - .02, dl[3] + .03, E.sine(seg(t, ...P.email))), g.decal.split || .6, 0];
      S.wet = 1 - seg(t, P.name[1], P.email[1] + 1.4);
      const pour = seg(t, ...P.pour);
      S.wax = { reveal: 1.2 * E.out(pour), mix: 0, spread: 1, glint: 0, molten: 1 - seg(t, ...P.molten), show: pour > 0, hover };
      if (mode === 'checking') S.wax.molten = Math.max(S.wax.molten, .45 + .3 * Math.sin(clock * 3.2));
      S.spoon = { show: t > P.spoonIn[0] && t < P.spoonOut[1], a: E.out(seg(t, ...P.spoonIn)), b: E.in2(seg(t, ...P.spoonOut)), pour };
      const sl = seg(t, 0, .2), sg = seg(t, .12, .62);
      S.stamp = {
        x: g.sx + 70 * E.in2(sg), y: g.sy - 150 * E.in2(sg), z: .3 + 10 * E.out(sl) + 640 * E.in2(sg), rz: .1 * E.in2(sg),
        rx: -.42 * E.out(sl) * (1 - sg), ry: .3 * E.out(sl) * (1 - sg), sc: 1, alpha: 1 - E.in2(seg(t, .3, .62)), on: false
      };
      S.stamper = { show: false };
      S.pm = 0; S.relief = { show: false };
      if (q >= 0) {
        const sd = seg(q, ...SP.down), su = seg(q, ...SP.up);
        S.stamper = { show: su < 1, d: sd, u: su, q, alpha: E.out(seg(q, 0, .16)) * (1 - seg(q, ...SP.toolFade)) };
        S.wax.mix = pressed ? 1 : E.io(seg(q, ...SP.mix)); S.wax.spread = 1 + .045 * E.out(seg(q, ...SP.spread)); S.wax.glint = seg(q, ...SP.glint);
        S.wax.hover = hover * (1 - seg(q, 0, .3)); S.wax.molten *= 1 - seg(q, 0, .4);
        const dip = seg(q, ...SP.dip); ez -= 2.2 * Math.sin(Math.PI * dip);
        const lf = seg(q, ...SP.lift), fp = seg(q, ...SP.flip);
        ez += 64 * E.out(lf); ery = Math.PI * E.io(fp); erz += -.035 * Math.sin(Math.PI * fp); erx += .07 * Math.sin(Math.PI * fp);
        ez -= 7 * Math.sin(Math.PI * seg(q, ...SP.pmDip));
        const fy = seg(q, ...SP.flight), fe = E.in(fy);
        ex += 90 * fe; ey -= 520 * fe; ez -= 1250 * fe; erz -= .16 * E.io(fy); erx -= .32 * E.io(fy);
        eo = 1 - E.in2(seg(q, ...SP.fade));
        S.pm = seg(q, SP.postmark, SP.postmark + .05); S.pmScale = 1 + .1 * (1 - E.out(seg(q, SP.postmark, SP.postmark + .3)));
        S.relief = { show: q > SP.relief[0], depth: E.out(seg(q, ...SP.relief)), sweep: E.sine(seg(q, ...SP.sweep)) };
      }
      S.e = { x: ex, y: ey, z: ez, rx: erx, ry: ery, rz: erz, o: eo };
      S.em = chain(M4.T(ex, ey, ez), M4.RZ(erz), M4.RY(ery), M4.RX(erx), M4.S(g.es), M4.T(-g.EW / 2, -g.EH / 2));
      if (q >= SP.stampIn[0] && mesh.stamp) {
        const ap = seg(q, ...SP.stampIn), on = g.stampOn, lx0 = g.EW - on.fx;
        const tgt = M4.apply(S.em, lx0, on.fy, -.7);
        const press = 1 + .05 * Math.sin(Math.PI * seg(q, ...SP.stampPress));
        if (ap < 1) S.stamp = { x: lerp(tgt[0] + 60, tgt[0], E.out(ap)), y: lerp(tgt[1] - 150, tgt[1], E.out(ap)), z: lerp(tgt[2] + 640, tgt[2], E.out(ap)), rz: lerp(.1, erz, E.out(ap)), rx: 0, ry: 0, sc: lerp(1, on.w * g.es, E.out(ap)) * press, alpha: E.out(seg(ap, 0, .4)), on: false };
        else S.stamp = { on: true, press, alpha: 1 };
      }
      return S;
    }
    const sealLocal = () => [G.EW / 2, G.sealY, 3.2];
    const poolLocal = () => { const w = WAX[waxIdx], Q = G.die / (2 * w.well), [sx, sy, sz] = sealLocal(); return [sx + (w.cx - w.wx) * Q, sy + (w.cy - w.wy) * Q, sz]; };
    function drawLetter(S, zr) {
      const g = G, m = S.letter.m;
      const o = { colF: COL.letter, colB: COL.letterBack, inkF: 1, inkB: 2, ink: T.ink, print: T.print, size: [g.W, g.H], deckle: 1.7, bump: .6, foldA: S.foldA, foldB: S.foldB };
      const all = [[0, g.hA], [g.W, g.hA], [g.W, g.hB], [0, g.hB]].map(([x, y]) => M4.apply(m, x, y, 0)).concat(panelPts(m, S.foldA, -1), g.thirds ? panelPts(m, S.foldB, 1) : []);
      castShadow(all, zr, 1, 2.5);
      drawPaper(mesh.letter, m, o, mesh.letter.mid);
      if (S.foldB[1] > 0) { castShadow(panelPts(m, S.foldB, 1), S.letter.z, .9); drawPaper(mesh.letter, m, o, mesh.letter.bot); }
      if (S.foldA[1] > 0) castShadow(panelPts(m, S.foldA, -1), S.letter.z + (S.foldB[1] > 2 ? 1 : 0), .9);
      drawPaper(mesh.letter, m, o, mesh.letter.top);
    }
    function drawStampFree(s) {
      if (!mesh.stamp || s.alpha <= .002) return;
      const g = G, m = chain(M4.T(s.x, s.y, s.z), M4.RZ(s.rz), M4.RX(s.rx), M4.RY(s.ry), M4.S(s.sc), M4.T(-g.sw / 2, -g.sh / 2));
      castShadow(corners(m, g.sw, g.sh), 0, s.alpha);
      drawPaper(mesh.stamp, m, { matF: 3, matB: 1, colB: COL.gum, ink: T.stamp, alphaTex: true, alpha: s.alpha, bump: .5 });
    }
    function flapPts(S, model) {
      const g = G, f = flapShape(g.EW, g.FHl, g.rt), out = [];
      for (const u of [0, g.tip * .35, g.tip * .7, g.tip * .92, g.tip]) {
        const w = f.w(u);
        for (const x of (w > .5 ? [g.EW / 2 - w, g.EW / 2 + w] : [g.EW / 2])) { const b = bendPt(x, -u, S.flap, -1); out.push(M4.apply(model, b[0], b[1], b[2])); }
      }
      return out;
    }
    function drawEnvelope(S) {
      const g = G, em = S.em, env = mesh.env, back = Math.cos(S.e.ry) >= 0;
      const flapM = chain(em, M4.T(0, 0, .9)), closing = S.flap[1] > Math.PI / 2;
      const foot = corners(em, g.EW, g.EH).concat(closing ? [] : flapPts(S, flapM));
      // once it flies off into the distance it no longer lies over the page: its shadow goes with it
      castShadow(foot, 0, clamp(1 + S.e.z / 120), 1.5);
      const paper = { colF: COL.envInside, colB: COL.env, bump: .8, rim: .05 };
      if (!back) {
        drawPaper(env.body, em, { ...paper, matF: 1, matB: 1, inkB: 3, ink: T.addr, texel: [.7 / g.EW / 2, .7 / g.EH / 2] });
        drawFront(S);
        return;
      }
      drawPaper(env.body, em, { ...paper, matF: 1, matB: 1 });
      drawPaper(env.liner, chain(em, M4.T(0, 0, .15)), { matF: 2, matB: 0, bump: .6 });
      const drawFlap = () => {
        drawPaper(env.flap, flapM, { ...paper, colF: COL.envInside, colB: COL.env, foldA: S.flap, matF: 1, matB: 1 });
        drawPaper(env.flapLiner, flapM, { matF: 2, matB: 0, foldA: S.flap, lift: .12, bump: .6 });
      };
      if (!closing) drawFlap();
      if (S.letter.show && S.letter.inside) drawLetter(S, S.e.z + .2);
      const pocketZ = chain(em, M4.T(0, 0, .6));
      for (const k of ['left', 'right', 'bottom']) {
        const pts = env.outlines[k].filter((_, i) => i % 3 === 0).map(([x, y]) => M4.apply(pocketZ, x, y, 0));
        castShadow(pts, S.e.z + .1, .85);
        drawPaper(env[k], pocketZ, { ...paper, colF: COL.env, colB: COL.envInside, matF: 1, matB: 1, rim: .07 });
      }
      if (closing) { castShadow(flapPts(S, flapM), S.e.z + .6, .9); drawFlap(); }
      if (S.reveal[0] > (g.decal.lines ? g.decal.lines[0] - .02 : 0) + .001) {
        const d = g.decal, m = chain(em, M4.T(d.x, d.y, 2.3));
        drawPaper(mesh.decal, m, { matF: 4, matB: 0, ink: T.back, reveal: S.reveal, wet: S.wet, inkCol: COL.ink, texel: [.7 / d.w / 2, .7 / d.h / 2], bump: .4 });
      }
      if (S.wax.show) {
        // the picture is scaled so its impression is exactly the seal stamp's face, its centre on the seal's spot
        const w = WAX[waxIdx], [sx, sy, sz] = sealLocal(), Q = g.die / (2 * w.well);
        const r = Q * w.r * Math.min(1, S.wax.reveal) * S.wax.spread;
        const pc = M4.apply(em, ...poolLocal());
        castShadow(circlePts(pc[0], pc[1], pc[2], r * g.es * .97), pc[2] - 2.6, .9);
        const m = chain(em, M4.T(sx, sy, sz), M4.S(Q), M4.T(-w.wx, -w.wy));
        drawSprite(m, { mode: 1, tex: T.pool, tex2: T.seal, geo: [w.cx, w.cy, w.r, 0], facing: true, reveal: S.wax.reveal, mix: S.wax.mix, spread: S.wax.spread, molten: S.wax.molten, hover: S.wax.hover, glint: S.wax.glint });
      }
    }
    function drawFront(S) {
      const g = G, em = S.em, on = g.stampOn;
      if (S.stamp.on && mesh.stamp) {
        const m = chain(em, M4.T(g.EW - on.fx, on.fy, -.7), M4.RY(Math.PI), M4.S(on.w * S.stamp.press), M4.T(-g.sw / 2, -g.sh / 2));
        castShadow(corners(m, g.sw, g.sh), S.e.z, .7 * clamp(1 + S.e.z / 120));
        drawPaper(mesh.stamp, m, { matF: 3, matB: 1, colB: COL.gum, ink: T.stamp, alphaTex: true, bump: .5 });
      }
      if (S.pm > 0) {
        const cxF = on.fx - g.sw * on.w / 2 + 4, cyF = on.fy + 6, sc = S.pmScale;
        const m = chain(em, M4.T(g.EW - cxF, cyF, -1.1), M4.RY(Math.PI), M4.S(sc), M4.T(-124, -43));
        drawPaper(mesh.pm, m, { matF: 5, matB: 0, ink: T.pm, inkCol: COL.postmark, alpha: .82 * S.pm });
      }
    }
    function drawSpoon(S, poolW) {
      const s = S.spoon; if (!s.show) return;
      const g = G, size = g.D * .78 / (2 * SPOON.r) * g.es;
      const tx = poolW[0] + g.D * g.es * .12, ty = poolW[1] - g.D * g.es * .16;
      const x = lerp(tx + 270, tx, s.a) + 260 * s.b, y = lerp(ty - 300, ty, s.a) - 300 * s.b, z = lerp(230, 64, s.a) + 170 * s.b;
      const rz = lerp(.24, 0, s.a) - .2 * Math.sin(Math.PI * Math.min(1, s.pour * 1.1)) + .12 * s.b;
      const alpha = Math.min(1, s.a * 3) * (1 - seg(s.b, .55, 1));
      const m = chain(M4.T(x, y, z), M4.RZ(rz), M4.S(size, size, 1), M4.T(-SPOON.lip[0], -SPOON.lip[1]));
      const bowl = M4.apply(m, SPOON.bowl[0], SPOON.bowl[1]), end = M4.apply(m, SPOON.end[0], SPOON.end[1]);
      castShadow(circlePts(bowl[0], bowl[1], z, SPOON.r * size, 10).concat([[end[0], end[1], z + 70], [end[0] + 6, end[1] + 6, z + 70]]), poolW[2] - 2, alpha * .9);
      if (s.pour > 0 && s.pour < .95) {
        const top = [x, y, z], bot = poolW, k = seg(s.pour, .78, .95);
        const p0 = [lerp(top[0], bot[0], k), lerp(top[1], bot[1], k), lerp(top[2], bot[2], k)];
        const dx = bot[0] - p0[0], dy = bot[1] - p0[1], len = Math.max(1, Math.hypot(dx, dy) + Math.abs(p0[2] - bot[2]) * .06), ang = Math.atan2(dy, dx) - Math.PI / 2;
        const wdt = g.D * g.es * .07 * (1 - k * .6);
        drawSprite(chain(M4.T(p0[0], p0[1], p0[2]), M4.RZ(ang), M4.S(wdt, len, 1), M4.T(-.5, 0)), { mode: 2, alpha: Math.min(1, s.pour * 8) });
      }
      drawSprite(m, { tex: T.spoon, alpha, tint: true });
    }
    // The seal stamp: arrives leaning in from the upper right, turning, and straightens as it comes down, so it meets the
    // wax upright, its face flat on the impression (seen from above: the ebony knob over the brass head); rocks a little
    // on the wax with a slight turn; rises straight, then tips away as it is lifted. Its face's centre is the model's
    // origin, so it lands exactly on the impression.
    function toolModel(s, sealW) {
      const a = E.in(s.d), lean = 1 - E.out(s.d), up = E.out(s.u), up2 = up * up, rock = seg(s.q, .5, .74), ring = Math.sin(Math.PI * rock);
      const x = sealW[0] + 34 * (1 - a) + 26 * up2, y = sealW[1] - 48 * (1 - a) - 52 * up2, z = sealW[2] + 400 * (1 - a) + 400 * up - .9 * ring;
      const tx = .62 * lean + .035 * Math.sin(rock * Math.PI * 2) * ring + .4 * up2;
      const ty = .34 * lean + .035 * Math.cos(rock * Math.PI * 2) * ring - .3 * up2;
      const tz = 1.1 * (1 - a) + .16 * ring - .6 * up;
      return chain(M4.T(x, y, z), M4.RX(tx), M4.RY(ty), M4.RZ(tz));
    }
    function drawTool(S, sealW) {
      const s = S.stamper; if (!s.show) return;
      const h = G.die * G.es;
      if (!mesh.tool || Math.abs(toolH - h) > .5) { freeMesh(mesh.tool); mesh.tool = buildTool(h); toolH = h; }
      const m = toolModel(s, sealW), head = M4.apply(m, 0, 0, h * .1), top = M4.apply(m, 0, 0, h * 2.3);
      castShadow(circlePts(head[0], head[1], head[2], h * .5, 12).concat(circlePts(top[0], top[1], top[2], h * .34, 10)), sealW[2] - 1, s.alpha * 2.6);
      const P = PTOOL; gl.useProgram(P.p); camera(P);
      gl.uniformMatrix4fv(U(P, 'u_model'), false, m);
      gl.uniform3fv(U(P, 'u_light'), LIGHT); gl.uniform1f(U(P, 'u_opacity'), s.alpha); gl.uniform1f(U(P, 'u_h'), h);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, paperMap); gl.uniform1i(U(P, 'u_map'), 0);
      gl.bindVertexArray(mesh.tool.vao);
      // depth first, then colour on the nearest surface only: solid when opaque, and no see-through while it fades
      gl.clear(gl.DEPTH_BUFFER_BIT); gl.enable(gl.DEPTH_TEST);
      gl.colorMask(false, false, false, false); gl.depthFunc(gl.LESS); gl.drawElements(gl.TRIANGLES, mesh.tool.count, gl.UNSIGNED_INT, 0);
      gl.colorMask(true, true, true, true); gl.depthFunc(gl.LEQUAL); gl.drawElements(gl.TRIANGLES, mesh.tool.count, gl.UNSIGNED_INT, 0);
      gl.disable(gl.DEPTH_TEST);
    }
    function drawRelief(S) {
      const g = G, R = g.relief, P = PR, r = S.relief;
      gl.useProgram(P.p); camera(P);
      gl.uniformMatrix4fv(U(P, 'u_model'), false, chain(M4.T(g.ex - R.w / 2, g.ey - R.h * .55, 0), M4.S(R.w, R.h, 1)));
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, T.relief); gl.uniform1i(U(P, 'u_tex'), 0);
      gl.uniform2f(U(P, 'u_texel'), 2 / (R.w * 2), 2 / (R.h * 2));
      gl.uniform1f(U(P, 'u_depth'), r.depth); gl.uniform1f(U(P, 'u_opacity'), 1);
      const a = lerp(-2.6, -1.75, r.sweep);
      gl.uniform3f(U(P, 'u_lightR'), Math.cos(a), Math.sin(a), .9); gl.uniform3fv(U(P, 'u_tint'), COL.tint);
      gl.bindVertexArray(QUAD); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    function render() {
      if (!alive || gl.isContextLost()) return;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE);
      if (!G || !paperMap) return;
      if (mode === 'idle') {
        const m = M4.T(G.lx, G.ly, 0);
        castShadow(corners(m, G.W, G.H), 0, 1, 2.5);
        drawPaper(mesh.letter, m, { colF: COL.letter, colB: COL.letterBack, print: T.print, size: [G.W, G.H], deckle: 1.7, bump: .6 });
        return;
      }
      const S = pose();
      if (S.relief.show) drawRelief(S);
      if (!S.envShow) drawLetter(S, 0);
      else {
        const fade = S.e.o < .999;
        if (fade) layerOn();
        drawEnvelope(S);
        if (fade) layerOff(S.e.o);
        if (S.letter.show && !S.letter.inside) drawLetter(S, S.e.z + .2);
      }
      const sealW = M4.apply(S.em, ...sealLocal());
      if (!S.stamp.on) drawStampFree(S.stamp);
      drawSpoon(S, M4.apply(S.em, ...poolLocal()));
      drawTool(S, sealW);
    }

    // time
    function kick() { if (!raf && alive) raf = requestAnimationFrame(loop); }
    function loop(now) {
      raf = 0;
      if (!alive) return;
      const dt = last ? Math.min(.05, (now - last) / 1000) : 1 / 60; last = now; clock += dt;
      let busy = false;
      if (mode === 'prep') { tp += dt; if (tp >= PT.ready) mode = 'sealed'; busy = true; }
      else if (mode === 'sealed' || mode === 'checking') { tp += dt; busy = tp < PT.molten[1] || mode === 'checking'; }
      else if (mode === 'press') { ts = Math.min(SP.hold, ts + dt); if (ts > SP.mix[1]) pressed = true; busy = ts < SP.hold; }
      else if (mode === 'send') { ts += dt; if (ts >= SP.end) { ts = SP.end; mode = 'done'; } else busy = true; }
      else if (mode === 'rewind') { tp -= dt * 2.2; if (tp <= 0) { tp = 0; mode = 'idle'; } else busy = true; }
      const h0 = hover; hover += (hoverTarget - hover) * Math.min(1, dt * 9);
      if (Math.abs(hoverTarget - hover) > .003) busy = true; else hover = hoverTarget;
      render();
      for (let i = waiters.length - 1; i >= 0; i--) if (waiters[i].test()) { const w = waiters.splice(i, 1)[0]; w.res(); }
      if (busy || h0 !== hover || waiters.length) kick(); else last = 0;
    }

    // set up: the paper, then the idle sheet behind the letter
    T.blank = tex(mkCanvas(1, 1), { mip: false });
    const ready = (async () => {
      await Promise.all([document.fonts ? document.fonts.ready : null, loadImg(assets.crest).then(i => { IMG.crest = i; })]);
      if (!alive) return;
      bakePaper();
      // the page's ready styles first (they move the name and email onto their lines), then the lines are printed
      // where the words now are; the letter can keep its height meanwhile, so no resize would report the move
      form.classList.add('is-v2-ready');
      layoutIdle(); render();
    })();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => { if (alive && mode === 'idle' && paperMap) { layoutIdle(); render(); } }) : null;
    if (ro) ro.observe(letterEl);
    const onResize = () => { if (alive && mode === 'idle' && paperMap) { layoutIdle(); render(); } };
    window.addEventListener('resize', onResize, { passive: true });
    const onFirst = () => prefetch();
    form.addEventListener('focusin', onFirst, { once: true });
    form.addEventListener('pointerdown', onFirst, { once: true });
    // Safari drops WebGL contexts under memory pressure or in the background, and this letter never rebuilds one:
    // hand the page back its own paper letter (the plain form still sends) instead of leaving a blank canvas.
    canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); alive = false; canvas.style.display = 'none'; form.classList.remove('is-v2-ready'); });

    return {
      ready,
      // folds the letter into the envelope, writes on it and pours the wax; resolves when the seal can be pressed.
      // taken() is called in the frame the canvas first draws the letter's words, for the page to hide its own.
      async start(taken) {
        await ready;
        if (!alive || !(await buildSend())) return false;
        mode = 'prep'; tp = 0; ts = -1; pressed = false; hover = hoverTarget = 0;
        render();
        if (taken) taken();
        await waitFor(() => mode !== 'prep');
        return alive;
      },
      checking(on) { if (mode === 'sealed' || mode === 'checking') { mode = on ? 'checking' : 'sealed'; kick(); } },
      hover(v) { if (mode === 'sealed') { hoverTarget = v; kick(); } },
      // the seal stamp presses the crest into the wax; resolves once it has lifted away
      async press() {
        mode = 'press'; ts = 0; kick();
        await waitFor(() => ts >= SP.up[1] + .05 || !alive);
      },
      // after a refused send: the envelope stays sealed and the seal can be pressed again
      sealed() { if (mode === 'press') { mode = 'sealed'; ts = -1; kick(); } },
      // Shopify has the letter: the envelope turns, is stamped and postmarked, and flies; resolves as the engraving rises
      async send() {
        mode = 'send'; ts = Math.max(ts, SP.hold); kick();
        await waitFor(() => ts >= SP.done || mode === 'done' || !alive);
      },
      // back to the letter, played backwards; resolves once the letter lies as it was
      async rewind() {
        mode = 'rewind'; ts = -1; hoverTarget = hover = 0; kick();
        await waitFor(() => mode === 'idle' || !alive);
        if (alive) { layoutIdle(); render(); }
      },
      // where things are, in the form's coordinates: the envelope at rest, the seal, the engraving
      geometry() {
        if (!G) return null;
        const D = G.D * G.es, em = chain(M4.T(G.ex, G.ey, 0), M4.S(G.es), M4.T(-G.EW / 2, -G.EH / 2)), c = M4.apply(em, G.EW / 2, G.sealY, 0);
        return { envelope: { top: G.envTop, bottom: G.envTop + G.EHs }, seal: { x: c[0], y: c[1], d: D }, relief: { top: G.ey - G.relief.h * .55, bottom: G.ey + G.relief.h * .45 } };
      },
      destroy() {
        alive = false;
        cancelAnimationFrame(raf);
        if (ro) ro.disconnect();
        window.removeEventListener('resize', onResize);
        form.removeEventListener('focusin', onFirst);
        form.removeEventListener('pointerdown', onFirst);
        form.classList.remove('is-v2-ready');
        letterEl.classList.remove('has-ink'); letterEl.style.removeProperty('--mp-ink');
        letterEl.querySelectorAll(INK).forEach(el => { el.style.removeProperty('--ink-x'); el.style.removeProperty('--ink-y'); });
        if (inkURL) URL.revokeObjectURL(inkURL);
        waiters.splice(0).forEach(w => w.res());
        const lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
        canvas.remove();
      }
    };
  }

  window.MenasaLetterV2 = { supported, create };
  document.dispatchEvent(new CustomEvent('menasa:letter-v2'));
})();
